/**
 * Builder for rebooking request payloads
 */
import { useErrorStore } from '../../stores/errorStore'
import {
  extractProductCodes,
  getProductServiceName,
  isNotCancelled,
  collectCancelIds,
  findOriginalItem,
  filterRebookableProducts,
  getOriginalProductInfoByPassenger,
  getAllAdditionalProducts,
  identifyUnavailableProducts,
  addUnavailableProductsToReplace,
  processAdditionalProductsForRebooking,
  isProductAvailableForService
} from '../../utils/rebooking/productUtils'

export const globalUsedExistingItemIds = new Set<string>()
import { matchJourneys, SegmentMapping } from '../../utils/rebooking/rebookingValidationUtils'
import { 
  RebookingAdditionalProduct,
  Direction,
  RebookingApiPassenger as Passenger,
  RebookingApiSegment as Segment,
  RebookingApiItem as Item,
  RebookingReplacement as ReplacementMapping
} from '../../types/rebookingTypes'
import { Route } from '../../types/travelTypes'
import { PassengerFare } from '../../types/passengerTypes'
import { resetItemIdCounter, createRebookingItem } from '../../utils/rebooking/rebookingItemUtils'

/**
 * Extracts station codes from a segment object
 * @param segment The segment object containing departure and arrival station information
 * @returns Object containing departureCode and arrivalCode
 */
const extractStationCodesFromSegment = (segment: any) => {
  let departureCode = '';
  let arrivalCode = '';
  
  if (segment) {
    if (segment.departure_station_code && segment.arrival_station_code) {
      departureCode = segment.departure_station_code;
      arrivalCode = segment.arrival_station_code;
    }
    else if (segment.departure_station && segment.arrival_station) {
      departureCode = segment.departure_station.code || '';
      arrivalCode = segment.arrival_station.code || '';
    }
    else if (segment.service_identifier) {
      const parts = segment.service_identifier.split(';');
      if (parts.length >= 3) {
        departureCode = parts[1] || '';
        arrivalCode = parts[2] || '';
      }
    }
  }
  
  return { departureCode, arrivalCode };
}

/**
 * Extract direction (OUTBOUND/INBOUND) from a segment ID
 */
const getDirectionFromSegmentId = (segmentId: string): string => {
  if (!segmentId) return ''
  
  if (segmentId.includes('OUTBOUND')) {
    return 'OUTBOUND'
  } else if (segmentId.includes('INBOUND')) {
    return 'INBOUND'
  }
  
  if (segmentId.includes('_OUT_') || segmentId.includes('_out_')) {
    return 'OUTBOUND'
  } else if (segmentId.includes('_IN_') || segmentId.includes('_in_')) {
    return 'INBOUND'
  }
  
  return ''
}

/**
 * Get the original main fare product for a passenger and direction
 */
const getOriginalMainFareProduct = (
  bookingData: any,
  passengerRefId: string,
  direction: Direction
): { productCode: string; tariffCode: string; source: string } | null => {
  const directionStr = direction === Direction.OUTBOUND ? 'OUTBOUND' : 'INBOUND'
  
  const items = bookingData?.items || []
  
  const mainFareItems = items.filter((item: any) => {
    if (item.passenger_ref_id !== passengerRefId || !item.segment_ref_id) {
      return false
    }
    
    const itemDirection = getDirectionFromSegmentId(item.segment_ref_id)
    if (itemDirection !== directionStr) {
      return false
    }
    
    const productCode = item.product_code || item.code
    if (!productCode) {
      return false
    }
    

    // ST = Seat & Ticket, TO = Ticket Only
    // We want to include only main fare products (typically TO type)
    const productType = item.type || item.inventory_class

    return productType === 'ST'
  })
  
  if (mainFareItems.length > 0) {
    const mainFareItem = mainFareItems[0]
    return {
      productCode: mainFareItem.product_code || mainFareItem.code,
      tariffCode: mainFareItem.tariff_code || mainFareItem.product_code || mainFareItem.code,
      source: 'original_booking'
    }
  }
  
  return null
}

/**
 * Interface for item adjustments in the API request
 */
interface ItemAdjustments {
  replacements: ReplacementMapping[];
  cancel_item_ids: string[];
}

/**
 * Helper function to extract product information from booking segments
 *
 * @param segments Array of booking segments to extract product info from
 * @param direction Direction of the journey (outbound/inbound) - kept for API consistency
 * @param collections Object containing collections to store extracted data
 */
const extractProductInfoFromSegments = (
  segments: any[] | null | undefined,
  direction: string,
  collections: {
    actualBookingProducts: Set<string>;
    actualBookingProductUuids: Set<string>;
    originalBookingRequiredProductsByRef: Map<string, any>;
    originalBookingTariffCodesBySegment: Map<string, Set<string>>;
  }
) => {
  if (!Array.isArray(segments)) {
    console.warn(`[WARN] extractProductInfoFromSegments called with non-array segments (${direction}):`, 
      segments, typeof segments)
    return
  }
  
  segments.forEach((segment: any, index: number) => {
    if (!segment) {
      console.warn(`[WARN] Segment at index ${index} is null or undefined in ${direction} segments`)
      return
    }
    
    const segmentId = Array.isArray(segment.booking_journey_segments) && 
      segment.booking_journey_segments.length > 0 ? 
      segment.booking_journey_segments[0]?.id || '' : ''
    
    if (segmentId) {
      if (!collections.originalBookingTariffCodesBySegment.has(segmentId)) {
        collections.originalBookingTariffCodesBySegment.set(segmentId, new Set<string>())
      }
    }

    try {
      if (segment.required_products !== undefined && !Array.isArray(segment.required_products)) {
        console.warn(`[WARN] segment.required_products is not an array in ${direction} segment ${index}:`, 
          typeof segment.required_products, segment.required_products)
      }
      
      const requiredProducts = Array.isArray(segment.required_products) ? 
        segment.required_products : []
      
      requiredProducts.forEach((product: any) => {
        if (!product) return
        
        if (product.code) collections.actualBookingProducts.add(product.code)
        if (product.uuid) collections.actualBookingProductUuids.add(product.uuid)
        if (product.item_ref)
          collections.originalBookingRequiredProductsByRef.set(
            String(product.item_ref),
            product
          )

        if (segmentId && product.tariff_code) {
          collections.originalBookingTariffCodesBySegment
            .get(segmentId)
            ?.add(product.tariff_code)
        }
      })
    } catch (error) {
      console.error(`[ERROR] Failed to process required_products for ${direction} segment ${index}:`, error)
    }
    
    try {
      if (segment.additional_products !== undefined && !Array.isArray(segment.additional_products)) {
        console.warn(`[WARN] segment.additional_products is not an array in ${direction} segment ${index}:`, 
          typeof segment.additional_products, segment.additional_products)
      }
      
      const additionalProducts = Array.isArray(segment.additional_products) ? 
        segment.additional_products : []
      
      additionalProducts.forEach((product: any) => {
        if (!product) return
        
        if (product.code) collections.actualBookingProducts.add(product.code)
        if (product.uuid) collections.actualBookingProductUuids.add(product.uuid)
        if (product.item_ref)
          collections.originalBookingRequiredProductsByRef.set(
            String(product.item_ref),
            product
          )
        if (segmentId && product.tariff_code) {
          collections.originalBookingTariffCodesBySegment
            .get(segmentId)
            ?.add(product.tariff_code)
        }
      })
    } catch (error) {
      console.error(`[ERROR] Failed to process additional_products for ${direction} segment ${index}:`, error)
    }
  })
}

/**
 * Checks if a segment is active by verifying it has active required products
 * A segment is considered active if:
 * 1. It's not explicitly marked as cancelled
 * 2. It has at least one non-cancelled required product
 * 
 * @param segment The booking segment to check
 * @returns boolean indicating if the segment is active
 */
const isSegmentActive = (segment: any): boolean => {
  if (segment.cancelled === true) {
    return false;
  }
  
  if (!Array.isArray(segment.required_products) || segment.required_products.length === 0) {
    return false;
  }
  
  const hasActiveProducts = Array.isArray(segment.required_products) && segment.required_products.some((product: any) => isNotCancelled(product));
  
  return hasActiveProducts;
}

/**
 * Extract journey IDs from booking data
 * Maps segment UUIDs to their corresponding journey IDs from booking_journey_segments
 * Only includes active segments (those with non-cancelled required products)
 * 
 * @param booking
 * @returns Record of segment UUIDs mapped to their journey IDs
 */
const extractJourneyIdsFromBooking = (booking: any): Record<string, string> => {
  const { outbound_booking_tariff_segments = [], inbound_booking_tariff_segments = [] } = booking || {};
  const journeyIdMap: Record<string, string> = {};
  
  if (Array.isArray(outbound_booking_tariff_segments)) {
    outbound_booking_tariff_segments.forEach(segment => {
    if (isSegmentActive(segment) && Array.isArray(segment.booking_journey_segments) && segment.booking_journey_segments.length > 0) {
      journeyIdMap[segment.uuid] = segment.booking_journey_segments[0].id;
    }
    });
  }
  
  if (Array.isArray(inbound_booking_tariff_segments)) {
    inbound_booking_tariff_segments.forEach(segment => {
    if (isSegmentActive(segment) && Array.isArray(segment.booking_journey_segments) && segment.booking_journey_segments.length > 0) {
      journeyIdMap[segment.uuid] = segment.booking_journey_segments[0].id;
    }
    });
  }
  
  return journeyIdMap;
};


/**
 * Logs detailed information about the selected travel data to help with debugging
 * @param selectedTravel The selected travel data
 * @param expectedRouteIndex The expected route index that should be used
 */
const logSelectedTravelDetails = (selectedTravel: any, expectedRouteIndex: number) => {
  if (!selectedTravel) {
    console.warn('[WARN] selectedTravel is null or undefined')
    return
  }
  
  if (Array.isArray(selectedTravel.routes)) {
    selectedTravel.routes.forEach((route: any, idx: number) => {
      if (!route) {
        console.log(`[DEBUG] Route ${idx}: null or undefined`)
        return
      }
      
      const hasBundles = Array.isArray(route.bundles) && route.bundles.length > 0
      
      if (idx === expectedRouteIndex) {
        
        if (hasBundles) {
          route.bundles.forEach((bundle: any, bundleIdx: number) => {
            if (bundle) {
            }
          })
        }
      }
    })
  }
}

/**
 * Process journey legs and add segments and items to the API request
 * 
 * @param legs Array of journey legs to process
 * @param direction 'outbound' or 'inbound'
 * @param bookingData Original booking data
 * @param segmentsData Original booking segments data
 * @param apiRequest API request being built
 * @param originalBookingTariffCodesBySegment Map of tariff codes by segment
 * @param newJourneyProductsByService Map of products by service
 * @param itemsToReplace List of items to replace
 * @param selectedTravel The selected travel data containing routes, legs, and bundles
 * @param journeyIdMap Mapping of segment UUIDs to journey IDs
 */
const processJourneyLegs = (
  legs: any[],
  direction: Direction,
  bookingData: any,
  segmentsData: any[],
  apiRequest: any,
  originalBookingTariffCodesBySegment: Map<string, Set<string>>,
  newJourneyProductsByService: Map<string, Set<string>>,
  itemsToReplace: string[] = [],
  selectedTravel: any = null,
  journeyIdMap: Record<string, string>
) => {
  let legsToProcess = legs;
  let legsSource = 'generic journey legs';
  
  if (selectedTravel && Array.isArray(selectedTravel.routes) && 'routeIndex' in selectedTravel) {
    const routeIndex = selectedTravel.routeIndex;
    const selectedRoute = selectedTravel.routes[routeIndex];
    
    if (selectedRoute && Array.isArray(selectedRoute.legs) && selectedRoute.legs.length > 0) {
      const directionLegs = selectedRoute.legs.filter((leg: any) => {
        if (leg.direction) {
          return leg.direction.toUpperCase() === direction.toString();
        }
        return true;
      });
      
      
      if (directionLegs.length > 0) {
        legsToProcess = directionLegs;
        legsSource = `selected route at index ${routeIndex}`;
      } 
    } 
  }

  if (!legsToProcess.length) {
    return
  }

  const segmentMappings = matchJourneys(segmentsData, legsToProcess, direction.toString())
  
  if (segmentMappings.length === 0) {
    console.error(`[ERROR] Could not find valid segment mappings for ${direction} journey`)
    console.error(`[ERROR DETAIL] Available segments: ${segmentsData.length}, Legs: ${legs.length}`)
    
    if (segmentsData.length > 0 && legsToProcess.length > 0) {
      const fallbackMappings = [];
      const minLength = Math.min(segmentsData.length, legsToProcess.length);
      
      for (let i = 0; i < minLength; i++) {
        fallbackMappings.push({
          originalSegmentId: segmentsData[i].uuid || segmentsData[i].id,
          newLegIndex: i,
          matchType: 'fallback' as 'fallback',
          allNewLegs: false
        });
      }
      
      if (fallbackMappings.length > 0) {
        segmentMappings.push(...fallbackMappings);
      }
    }
    
    if (segmentMappings.length === 0) {
      const errorMessage = `Could not find valid segment mappings for ${direction} journey`
      console.error(`[ERROR] ${errorMessage}`)
      
      const errorStore = useErrorStore()
      errorStore.setError('Kunne ikke finne passende segment fra originalbooking for den nye reisen. Vennligst kontakt kundeservice.')
      throw new Error(errorMessage)
    }
  }
  
  for (const mapping of segmentMappings) {
    if (mapping.matchType === 'exact') {
      const legIndex = mapping.newLegIndex!
      const leg = legsToProcess[legIndex]
      const matchingSegment = segmentsData.find(s => 
        s.uuid === mapping.originalSegmentId || s.id === mapping.originalSegmentId)

      if (!matchingSegment) {
        continue
      }
      
      processLegWithMatchingSegment(
        leg,
        legIndex,
        direction,
        bookingData,
        matchingSegment,
        apiRequest,
        originalBookingTariffCodesBySegment,
        newJourneyProductsByService,
        itemsToReplace,
        selectedTravel,
        journeyIdMap
      )
    } 
    else if (mapping.matchType === 'partial' || mapping.matchType === 'fallback') {
      const matchingSegment = segmentsData.find(s => 
        s.uuid === mapping.originalSegmentId || s.id === mapping.originalSegmentId)
      
      if (!matchingSegment) {
        console.warn(`[WARN] Could not find matching segment for ID ${mapping.originalSegmentId}`)
        continue
      }
      
      if (mapping.allNewLegs) {
        for (let legIndex = 0; legIndex < legsToProcess.length; legIndex++) {
          const leg = legsToProcess[legIndex]
          
          const hasExactMatch = segmentMappings.some((m: SegmentMapping) => 
            m.matchType === 'exact' && m.newLegIndex === legIndex)
          
          if (!hasExactMatch) {
            processLegWithMatchingSegment(
              leg,
              legIndex,
              direction,
              bookingData,
              matchingSegment,
              apiRequest,
              originalBookingTariffCodesBySegment,
              newJourneyProductsByService,
              itemsToReplace,
              selectedTravel,
              journeyIdMap
            )
          }
        }
      }
      else if (mapping.newLegIndex !== undefined) {
        const legIndex = mapping.newLegIndex
        const leg = legsToProcess[legIndex]
        
        processLegWithMatchingSegment(
          leg,
          legIndex,
          direction,
          bookingData,
          matchingSegment,
          apiRequest,
          originalBookingTariffCodesBySegment,
          newJourneyProductsByService,
          itemsToReplace,
          selectedTravel,
          journeyIdMap
        )
      }
    }
  }
}

/**
 * Helper function to process a leg with a matching segment
 */
const processLegWithMatchingSegment = (
  leg: any,
  legIndex: number,
  direction: Direction,
  bookingData: any,
  matchingSegment: any,
  apiRequest: any,
  originalBookingTariffCodesBySegment: Map<string, Set<string>>,
  newJourneyProductsByService: Map<string, Set<string>>,
  itemsToReplace: string[] = [],
  selectedTravel: any = null,
  journeyIdMap: Record<string, string>
) => {
  const legServiceName = leg.service_identifier ? leg.service_identifier.split('|')[1] : ''
  const legDepartureUicCode = leg.departure_station?._u_i_c_station_code || leg.departure_station?.short_code || ''
  const legArrivalUicCode = leg.arrival_station?._u_i_c_station_code || leg.arrival_station?.short_code || ''

  let segmentRefId = '';
  
  if (leg.id && leg.id.includes(';')) {
    segmentRefId = leg.id;
  }
  else if (leg.service_identifier) {
    segmentRefId = `${leg.service_identifier};${legDepartureUicCode};${legArrivalUicCode}`;
  }

  else {
    segmentRefId = matchingSegment.uuid || uuidv4();
  }

  const segmentAlreadyInRequest = apiRequest.segments.some(
    (segment: any) => segment.ref_id === segmentRefId || segment.reference === segmentRefId
  )

  if (!segmentAlreadyInRequest) {

    let formattedDate = ''
    if (leg.departure_date_time) {
      formattedDate = leg.departure_date_time.split('T')[0]
    } else if (leg.departure_timestamp) {
      formattedDate = leg.departure_timestamp.split('T')[0]
    } else if (leg.service_schedule_date) {
      formattedDate = leg.service_schedule_date.split('T')[0]
    } else if (matchingSegment?.travel_date) {
      formattedDate = matchingSegment.travel_date.split('T')[0]
    } else {
      const today = new Date()
      formattedDate = today.toISOString().split('T')[0]
    }
    const serviceIdentifierParts = (leg.service_identifier || '').split('|');
    const serviceType = serviceIdentifierParts[0] || '';
    const serviceName = serviceIdentifierParts[1] || '';
    
    let tariffGroupCode = '';
    
    if (selectedTravel && Array.isArray(selectedTravel.routes)) {
      const bundleRoute = selectedTravel.routes.find((route: Route) => 
        route && Array.isArray(route.bundles) && route.bundles.length > 0
      );
      
      if (bundleRoute && bundleRoute.bundles && bundleRoute.bundles.length > 0) {
        const firstBundle = bundleRoute.bundles[0];
        
        if (firstBundle.tariff_group_code) {
          tariffGroupCode = firstBundle.tariff_group_code;
        } else if (Array.isArray(firstBundle.items) && firstBundle.items.length > 0) {
          for (const item of firstBundle.items) {
            if (item.passenger_fares && Array.isArray(item.passenger_fares) && item.passenger_fares.length > 0) {
              const firstFare = item.passenger_fares[0];
              if (firstFare && firstFare.tariff_code) {
                tariffGroupCode = firstFare.tariff_code;
                break;
              }
            }
          }
        }
      }
    }
    
    const segment = {
      ref_id: segmentRefId,
      reference: segmentRefId,
      service_identifier: leg.service_identifier || '',
      service_name: serviceName,  
      service_type: serviceType,
      tariff_group_code: tariffGroupCode,
      schedule_date: formattedDate,
      travel_date: formattedDate,
      origin_station_code: legDepartureUicCode,
      destination_station_code: legArrivalUicCode,
      departure_datetime: leg.departure_date_time || '',
      arrival_datetime: leg.arrival_date_time || '',
      direction: direction.toString(),
      departure_station: {
        code_1: legDepartureUicCode,
        code_2: leg.departure_station?.short_code || '',
        name: leg.departure_station?.name || ''
      },
      arrival_station: {
        code_1: legArrivalUicCode,
        code_2: leg.arrival_station?.short_code || '',
        name: leg.arrival_station?.name || ''
      },
      transport: legServiceName || ''
    }
  
    apiRequest.segments.push(segment)

    const segmentUuid = matchingSegment.uuid || matchingSegment.id
    if (segmentUuid) {
      journeyIdMap[segmentUuid] = segmentRefId
    }
 
    addRequiredProductsForSegment(
      bookingData,
      apiRequest,
      segmentRefId,
      matchingSegment,
      newJourneyProductsByService,
      originalBookingTariffCodesBySegment,
      leg.service_identifier || '',
      direction,
      itemsToReplace,
      selectedTravel,
      journeyIdMap
    )
    
    // Process additional products if available
    if (matchingSegment.additional_products) {
      // Filter to only include essential additional products based on their type or inventory class
      const essentialAdditionalProducts = matchingSegment.additional_products.filter((product: any) => {
        // Get product type and inventory class
        const productType = product.type || product.inventory_class || ''
        
        // Filter based on product type - typically additional products have types like 'AD' or 'AP'
        // Exclude seat products (ST) as they are handled separately
        return productType && productType !== 'ST' && productType !== 'TO'
      })
      
      if (essentialAdditionalProducts.length > 0) {
        processAdditionalProducts(
          essentialAdditionalProducts,
          segmentRefId,
          apiRequest,
          itemsToReplace,
          bookingData,
          selectedTravel,
          newJourneyProductsByService
        )
      }
    }
  } 
}

/**
 * Add required products for a segment to the API request
 */
const addRequiredProductsForSegment = (
  bookingData: any,
  apiRequest: any,
  segmentRefId: string,
  matchingSegment: any,
  newJourneyProductsByService: Map<string, Set<string>>,
  originalBookingTariffCodesBySegment: Map<string, Set<string>>,
  serviceIdentifier: string,
  direction: Direction,
  itemsToReplace: string[] = [],
  selectedTravel: any = null,
  journeyIdMap: Record<string, string>
) => {
  if (!segmentRefId) {
    console.warn('[DEBUG] Missing segmentRefId in addRequiredProductsForSegment, generating fallback ID')
    segmentRefId = `segment_${direction.toString()}_fallback_${Date.now().toString().slice(-4)}`
  }

  const passengerRefs = Array.isArray(bookingData.passengers) ? bookingData.passengers.map((p: any) => {
    if (!p.id) {
      return 'passenger_1'
    }
    return p.id
  }) : []

  if (passengerRefs.length === 0) {
    console.error('No passengers found in booking data')
    return
  }


  if (Array.isArray(passengerRefs)) {
    passengerRefs.forEach((passengerRef: string) => {
      let itemsAdded = false
      let bundleItems: any[] = []; // Define bundleItems at this scope level
      
      try {
        if (!selectedTravel) {
          console.warn('[WARN] selectedTravel is null or undefined')
        } else if (!Array.isArray(selectedTravel.routes)) {
          console.warn('[WARN] selectedTravel.routes is not an array:', typeof selectedTravel.routes)
        } else if (selectedTravel.routes.length === 0) {
          console.warn('[WARN] selectedTravel.routes is empty')
        } else {
          const expectedRouteIndex = 'routeIndex' in selectedTravel ? selectedTravel.routeIndex : 0
          logSelectedTravelDetails(selectedTravel, expectedRouteIndex)
          
          const routeIndex = expectedRouteIndex
          let selectedRoute = null
          let bundleRoute = null
          
          selectedRoute = selectedTravel.routes[routeIndex]
          
          if (!selectedRoute) {
            console.error(`[ERROR] No route found at index ${routeIndex}. This is a critical error.`)
          } else {
            bundleRoute = selectedRoute

            const hasAvailability = selectedRoute.bundle_availability?.availability === true
            const hasLogicalAvailability = selectedRoute.bundle_availability?.logical_availability === true
            const hasBundles = Array.isArray(selectedRoute.bundles) && selectedRoute.bundles.length > 0
            
            console.log(`[DEBUG] Selected route at index ${routeIndex}:` +
                        ` availability=${hasAvailability},` +
                        ` logical_availability=${hasLogicalAvailability},` +
                        ` bundles=${hasBundles ? selectedRoute.bundles.length : 0}`)
            
            if (!hasBundles) {
              console.error(`[ERROR] Selected route at index ${routeIndex} has no bundles. This will cause rebooking to fail.`)
            } else if (!hasAvailability) {
              console.warn(`[WARN] Selected route at index ${routeIndex} has no availability. Proceeding anyway as requested.`)
            }
          }
          
          if (!bundleRoute) {
            console.warn('[WARN] No routes with valid bundles found in selectedTravel')
          } else if (!Array.isArray(bundleRoute.bundles)) {
            console.warn('[WARN] bundleRoute.bundles is not an array:', typeof bundleRoute.bundles)
          } else if (bundleRoute.bundles.length === 0) {
            console.warn('[WARN] bundleRoute.bundles is empty')
          } else {
            const bundleIndex = 0
            const selectedBundle = bundleRoute.bundles[bundleIndex]
            
            if (!selectedBundle) {
              console.warn(`[WARN] Bundle at index ${bundleIndex} in bundleRoute.bundles is null or undefined`)
            } else if (!Array.isArray(selectedBundle.items)) {
              console.warn(`[WARN] bundleRoute.bundles[${bundleIndex}].items is not an array:`, typeof selectedBundle.items)
            } else if (selectedBundle.items.length === 0) {
              console.warn(`[WARN] bundleRoute.bundles[${bundleIndex}].items is empty`)
            } else {
              bundleItems = (selectedBundle.items as Array<{product_code?: string, product_type?: string, passenger_fares?: Array<{tariff_code?: string, product_code?: string}>}>).filter((item) => {
                if (!item || !item.product_code) return false;
                
                return item.product_type === 'ST' || item.product_type === 'TO';
              })
            }
          }
        }
      } catch (error) {
        console.error('[ERROR] Exception while processing selectedTravel bundle items:', error)
      }

      if (bundleItems.length > 0) {
        for (let i = 0; i < bundleItems.length; i++) {
          const item = bundleItems[i]
          const productCode = item.product_code
          
          if (productCode) {
            // Check if this product matches one in the itemsToReplace list
            const matchesItemToReplace = Array.isArray(itemsToReplace) ? itemsToReplace.some(itemId => {
              const originalItem = findOriginalItem(itemId, bookingData)
              return originalItem?.code === productCode || originalItem?.product_code === productCode
            }) : false

            const shouldSkip = false
            if (!shouldSkip) {
              const direction = segmentRefId.includes('OUTBOUND') ? 'OUTBOUND' : 'INBOUND'
              const originalProductInfo = getOriginalProductInfoByPassenger(
                bookingData,
                passengerRef,
                direction as 'OUTBOUND' | 'INBOUND',
                true
              )
            
              let tariffCode = '';
            
              if (item.passenger_fares && Array.isArray(item.passenger_fares) && item.passenger_fares.length > 0) {
                const passengerFare = item.passenger_fares.find((fare: PassengerFare) => fare.passenger_id === passengerRef) || item.passenger_fares[0];
                if (passengerFare && passengerFare.tariff_code) {
                  tariffCode = passengerFare.tariff_code;
                } else {
                  console.error(`[ERROR] No tariff_code found in passenger_fares for product ${productCode}`);
                  continue;
                }
              } else {
                console.error(`[ERROR] No passenger_fares found for product ${productCode}`);
                continue;
              }
              
              const requiredItem = createRebookingItem(false, {
                passenger_ref_id: passengerRef,
                segment_ref_id: segmentRefId,
                product_code: productCode,
                tariff_code: tariffCode
              })
              
              
              apiRequest.items.push(requiredItem)
              itemsAdded = true
              
              const requiredItemRefId = requiredItem.ref_id;
              if (itemsToReplace && itemsToReplace.length > 0) {
                // Ensure item_adjustments and replacements are initialized
                if (!apiRequest.item_adjustments) {
                  apiRequest.item_adjustments = {}
                }
                if (!apiRequest.item_adjustments.replacements) {
                  apiRequest.item_adjustments.replacements = []
                }
                
                // Track which existing items have already been included in replacements
                const usedExistingItemIds = new Set<string>();
                
                // Filter items to replace to only include those for this passenger and direction
                const relevantItemsToReplace = itemsToReplace.filter(itemId => {
                  // Skip if already used in another replacement
                  if (usedExistingItemIds.has(itemId)) {
                    return false;
                  }
                  
                  const item = findOriginalItem(bookingData, itemId);
                  if (!item) return false;
                  
                  // Check if this item belongs to the current passenger
                  const matchesPassenger = item.passenger_ref_id === passengerRef;
                  
                  // Check if this item belongs to the current direction
                  const itemDirection = getDirectionFromSegmentId(item.segment_ref_id || '');
                  const currentDirection = getDirectionFromSegmentId(segmentRefId);
                  const matchesDirection = itemDirection === currentDirection;
                  
                  return matchesPassenger && matchesDirection;
                });
                
                if (relevantItemsToReplace.length > 0) {
                  // Validate that all items belong to the same passenger
                  const validPassenger = validateSamePassenger(relevantItemsToReplace, bookingData);
                  
                  // Validate that all items have the same direction
                  const validDirection = validateSameDirection(relevantItemsToReplace, bookingData);
                  
                  // Only create mapping if all validations pass
                  if (validPassenger && validDirection) {
                    // Filter out items that are already used in a replacement mapping
                    const unusedItems = relevantItemsToReplace.filter(id => !globalUsedExistingItemIds.has(id));
                    
                    if (unusedItems.length > 0) {
                      apiRequest.item_adjustments.replacements.push({
                        existing_item_ids: unusedItems,
                        added_item_ref_ids: [requiredItemRefId]
                      });
                      
                      // Mark these items as used in both local and global tracking
                      unusedItems.forEach(id => {
                        usedExistingItemIds.add(id);
                        globalUsedExistingItemIds.add(id);
                        console.log(`[DEBUG] Marked item ${id} as used in global tracking set`);
                      });
                    } else {
                      console.log(`[DEBUG] All items are already used in replacement mappings, skipping`);
                    }
                    
                    console.log(`[DEBUG] Added replacement mapping for ${relevantItemsToReplace.length} items with new item ${requiredItemRefId}`);
                  } else {
                    if (!validPassenger) {
                      console.error(`[ERROR] Cannot create replacement: items belong to different passengers`);
                    }
                    if (!validDirection) {
                      console.error(`[ERROR] Cannot create replacement: items have different directions`);
                    }
                    console.error(`[ERROR] Skipping replacement for items: ${relevantItemsToReplace.join(', ')}`);
                  }
                }
              }
              // If we successfully added an item, break out of the loop
              if (itemsAdded) {
                break;
              }
            }
          } else {
            // Log more details about the product evaluation
            console.log(`[DEBUG] Product ${productCode} evaluation:`, {
              matchesItemToReplace: matchesItemToReplace,
              shouldSkip: shouldSkip
            })
            console.log(`Skipping product ${productCode} - this should not happen with current logic`)
            // This should never happen with our current logic, but keeping for safety
          }
        }
      }
      
      // If no items were added, try to get products from newJourneyProductsByService
      if (!itemsAdded) {
        console.log(`[DEBUG] Attempting to find products for segment ${segmentRefId} using service identifier`)
        // Log the segment reference ID to ensure it's available
        console.log(`[DEBUG] Current segmentRefId: ${segmentRefId || 'UNDEFINED'}`)
        console.log('No items added from selected travel, trying products from journey map')
        const serviceName = serviceIdentifier ? 
          getProductServiceName({service_identifier: serviceIdentifier}) : 
          null

        if (serviceName) {
          console.log(`Got service name: ${serviceName} from identifier ${serviceIdentifier}`)
          const products = newJourneyProductsByService.get(serviceName)

          if (products && products.size > 0) {
            console.log(`Found ${products.size} products for service ${serviceName}`)
            
            // Get the original fare product for this passenger and segment if available
            const originalFareProduct = getOriginalMainFareProduct(bookingData, passengerRef, direction)
            const originalProductInfo = originalFareProduct
            
            // Filter products by type to include only main fare products (TO and ST type)
            const availableProducts = Array.from(products).filter(productCode => {
              try {
                // Check if products is a Map or a Set
                if (products instanceof Map) {
                  const productInfo = products.get(productCode)
                  if (!productInfo) return false
                  
                  const productType = productInfo.type || productInfo.inventory_class
                  
                  return productType === 'TO' || productType === 'ST' || productType === 'TICKET'
                } else {
                  if (productCode.includes('SEAT')) return false
                  
                  return ['STANDARD', 'FLEX'].some(fareType => 
                    productCode.includes(fareType))
                }
              } catch (error) {
                console.error(`[ERROR] Error filtering product ${productCode}:`, error)
                return false
              }
            })
            
            console.log(`[DEBUG] Filtered to ${availableProducts.length} potential main fare products from ${products.size} total products`)
            
            let preferredProduct = originalFareProduct ? 
              availableProducts.find(p => p === originalFareProduct.productCode) : null
            
            if (!preferredProduct && availableProducts.length > 0) {
              preferredProduct = availableProducts[0]
            }
            
            if (!preferredProduct) {
              const errorMessage = `No suitable main fare products found for service ${serviceName}. Cannot proceed with rebooking without a valid fare product.`
              console.error(`[ERROR] ${errorMessage}`)
              throw new Error(errorMessage)
            }
            
            itemsAdded = true

            if (itemsToReplace && itemsToReplace.length > 0) {
              if (!apiRequest.item_adjustments) {
                apiRequest.item_adjustments = {}
              }
              if (!apiRequest.item_adjustments.replacements) {
                apiRequest.item_adjustments.replacements = []
              }

              const relevantItemsToReplace = itemsToReplace.filter(itemId => {
                if (globalUsedExistingItemIds.has(itemId)) {
                  return false;
                }
                
                const item = findOriginalItem(bookingData, itemId);
                if (!item) return false;
                
                // Check if this item belongs to the current passenger
                const matchesPassenger = item.passenger_ref_id === safePassengerRef;
                
                // Check if this item belongs to the current direction
                const itemDirection = getDirectionFromSegmentId(item.segment_ref_id || '');
                const currentDirection = segmentDirection;
                const matchesDirection = itemDirection === currentDirection;
                
                return matchesPassenger && matchesDirection;
              });
              
              if (relevantItemsToReplace.length > 0) {
                // Validate that all items belong to the same passenger
                const validPassenger = validateSamePassenger(relevantItemsToReplace, bookingData);
                
                // Validate that all items have the same direction
                const validDirection = validateSameDirection(relevantItemsToReplace, bookingData);
                
                // Only create mapping if all validations pass
                if (validPassenger && validDirection) {
                  apiRequest.item_adjustments.replacements.push({
                    existing_item_ids: relevantItemsToReplace,
                    added_item_ref_ids: [itemRefId]
                  });
                  
                  // Mark these items as used in the global tracking mechanism
                  relevantItemsToReplace.forEach(id => globalUsedExistingItemIds.add(id));
                  
                  console.log(`[DEBUG] Added replacement mapping for ${relevantItemsToReplace.length} items with new item ${itemRefId}`);
                } else {
                  if (!validPassenger) {
                    console.error(`[ERROR] Cannot create replacement: items belong to different passengers`);
                  }
                  if (!validDirection) {
                    console.error(`[ERROR] Cannot create replacement: items have different directions`);
                  }
                  console.error(`[ERROR] Skipping replacement for items: ${relevantItemsToReplace.join(', ')}`);
                }
              }
            }
          } else {
            console.error(`No products found for service ${serviceName}`)
          }
        } else {
          console.error('Failed to get service name from identifier:', serviceIdentifier)
        }
      }

      // If no items were added for this passenger after trying both methods, log an error
      // We no longer use fallback products as they can cause availability errors
      if (!itemsAdded) {
        const errorMessage = `No valid products found for passenger ${passengerRef} on segment ${segmentRefId}`;
        console.error(`[ERROR] ${errorMessage}`);
        
        // Don't try to use the error store directly here as it's causing issues
        // Just throw an error which will be caught by the calling function
        // The error handling should be done at a higher level in the component
        
        // Throw an error to prevent proceeding with an invalid request
        throw new Error(`No valid fare products available for this journey segment. Please select a different route.`);
      }
    })
  } else {
    console.warn('[WARN] passengerRefs is not an array in addRequiredProductsForSegment')
  }
}

// Note: getDirectionFromSegmentId function is already defined at the top of the file

/**
 * Validate that all items belong to the same passenger
 * 
 * @param existingItemIds Array of existing item IDs
 * @param bookingData Original booking data
 * @returns True if all items belong to the same passenger, false otherwise
 */
const validateSamePassenger = (existingItemIds: string[], bookingData: any): boolean => {
  const passengerIds = new Set<string>();
  
  existingItemIds.forEach(itemId => {
    const item = findOriginalItem(bookingData, itemId);
    if (item && item.passenger_ref_id) {
      passengerIds.add(item.passenger_ref_id);
    }
  });
  
  return passengerIds.size <= 1;
};

/**
 * Validate that all items have the same direction (outbound/inbound)
 * 
 * @param existingItemIds Array of existing item IDs
 * @param bookingData Original booking data
 * @returns True if all items have the same direction, false otherwise
 */
const validateSameDirection = (existingItemIds: string[], bookingData: any): boolean => {
  const directions = new Set<string>();
  
  existingItemIds.forEach(itemId => {
    const item = findOriginalItem(bookingData, itemId);
    if (item && item.segment_ref_id) {
      // Find the segment for this item
      const segment = bookingData.segments?.find((s: any) => s.ref_id === item.segment_ref_id);
      if (segment && segment.direction) {
        directions.add(segment.direction);
      }
    }
  });
  
  return directions.size <= 1;
};

/**
 * Validate that an item belongs to a specific segment
 * 
 * @param item The item to check
 * @param segmentRefId The segment reference ID
 * @param bookingData Original booking data
 * @returns True if the item belongs to the specified segment, false otherwise
 */
const isItemForSegment = (item: any, segmentRefId: string, bookingData: any): boolean => {
  // If the item has a segment_ref_id, check it directly
  if (item.segment_ref_id) {
    return item.segment_ref_id === segmentRefId;
  }
  
  // If not, we need to find the segment this item belongs to
  // Check outbound segments
  if (bookingData?.outbound_booking_tariff_segments && Array.isArray(bookingData.outbound_booking_tariff_segments)) {
    for (const segment of bookingData.outbound_booking_tariff_segments) {
      // Check if this segment contains the item
      const foundInRequired = segment?.required_products?.some(
        (p: any) => p?.uuid === item.uuid || p?.id === item.uuid
      );
      
      const foundInAdditional = segment?.additional_products?.some(
        (p: any) => p?.uuid === item.uuid || p?.id === item.uuid
      );
      
      if (foundInRequired || foundInAdditional) {
        // Check if this segment matches the target segment
        const segmentId = segment.ref_id || segment.id;
        return segmentId === segmentRefId;
      }
    }
  }
  
  // Check inbound segments
  if (bookingData?.inbound_booking_tariff_segments && Array.isArray(bookingData.inbound_booking_tariff_segments)) {
    for (const segment of bookingData.inbound_booking_tariff_segments) {
      // Check if this segment contains the item
      const foundInRequired = segment?.required_products?.some(
        (p: any) => p?.uuid === item.uuid || p?.id === item.uuid
      );
      
      const foundInAdditional = segment?.additional_products?.some(
        (p: any) => p?.uuid === item.uuid || p?.id === item.uuid
      );
      
      if (foundInRequired || foundInAdditional) {
        // Check if this segment matches the target segment
        const segmentId = segment.ref_id || segment.id;
        return segmentId === segmentRefId;
      }
    }
  }
  
  return false;
};

/**
 * Process additional products (seats, meals, etc.) and add them to the API request
 * 
 * @param additionalProducts Array of additional products to rebook
 * @param segmentRefId Reference ID for the segment
 * @param apiRequest API request being built
 * @param itemsToReplace List of items to replace
 * @param bookingData Original booking data
 * @param selectedTravel Selected travel data
 */
const processAdditionalProducts = (
  additionalProducts: RebookingAdditionalProduct[],
  segmentRefId: string,
  apiRequest: any,
  itemsToReplace: string[] = [],
  bookingData: any,
  selectedTravel: any = null,
  newJourneyProductsByService?: Map<string, Set<string>>
) => {
  console.log(`[DEBUG] Processing additional products: ${additionalProducts?.length || 0} found`)
  
  // Ensure items array is initialized
  if (!apiRequest.items) {
    apiRequest.items = []
    console.log('[DEBUG] Initialized apiRequest.items array')
  }
  
  // Ensure item_adjustments and replacements are initialized
  if (!apiRequest.item_adjustments) {
    apiRequest.item_adjustments = {}
  }
  if (!apiRequest.item_adjustments.replacements) {
    apiRequest.item_adjustments.replacements = []
  }
  
  // Use the global tracking mechanism to avoid duplicates across functions
  // This ensures each original item is only replaced once in the entire request
  
  // Get the current segment direction
  const currentSegmentDirection = getDirectionFromSegmentId(segmentRefId);
  console.log(`[DEBUG] Processing additional products for segment direction: ${currentSegmentDirection}`);
  
  // Group products by passenger, direction, and product code for deduplication
  const groupedProducts = new Map<string, RebookingAdditionalProduct[]>();
  
  // First, group products by passenger, segment, and product code
  additionalProducts.forEach((product) => {
    // Get passenger reference ID from the product or find it in the booking data
    // Don't use hardcoded fallbacks
    let passengerRefId = product.passenger_ref_id;
    
    if (!passengerRefId && apiRequest.passengers && apiRequest.passengers.length > 0) {
      // Try to find a matching passenger in the API request
      const matchingPassenger = apiRequest.passengers.find((p: any) => 
        p.uuid === product.passenger_uuid || p.id === product.passenger_id);
      
      if (matchingPassenger) {
        passengerRefId = matchingPassenger.reference || matchingPassenger.ref_id;
      } else {
        // If we still don't have a passenger reference, log an error and skip this product
        console.error(`[ERROR] Could not determine passenger reference ID for product ${product.code || product.product_code}`);
        return; // Skip this product
      }
    }
    
    // If we still don't have a valid passenger reference ID, skip this product
    if (!passengerRefId) {
      console.error(`[ERROR] Missing passenger reference ID for product ${product.code || product.product_code}`);
      return; // Skip this product
    }
    
    const productCode = product.code || product.tariff_code;
    // Use segment-specific information instead of just direction
    // Include the segment reference ID to ensure proper segment boundaries
    const groupKey = `${passengerRefId}_${segmentRefId}_${productCode}`;
    
    if (!groupedProducts.has(groupKey)) {
      groupedProducts.set(groupKey, []);
    }
    
    groupedProducts.get(groupKey)?.push(product);
  });
  
  // Process only one product per passenger, direction, and product code
  groupedProducts.forEach((products, groupKey) => {
    // Log the grouped products for debugging
    console.log(`[DEBUG] Found ${products.length} products for group key: ${groupKey}`);
    
    if (products.length > 1) {
      console.log(`[WARNING] Found ${products.length} duplicate products for ${groupKey}. Using only the first non-cancelled one.`);
    }
    
    // Use the first product that's not cancelled, or just the first one if all are in the same state
    const productToUse = products.find(p => p.status !== 'cancelled') || products[0];
    
    // Get passenger reference ID without hardcoded fallbacks
    let passengerRefId = productToUse.passenger_ref_id;
    
    if (!passengerRefId && apiRequest.passengers && apiRequest.passengers.length > 0) {
      // Try to find a matching passenger in the API request
      const matchingPassenger = apiRequest.passengers.find((p: any) => 
        p.uuid === productToUse.passenger_uuid || p.id === productToUse.passenger_id);
      
      if (matchingPassenger) {
        passengerRefId = matchingPassenger.reference || matchingPassenger.ref_id;
      } else {
        // If we still don't have a valid passenger reference, skip this product group
        console.error(`[ERROR] Could not determine passenger reference ID for product group ${groupKey}`);
        return; // Skip this product group
      }
    }
    
    // If we still don't have a valid passenger reference ID, skip this product group
    if (!passengerRefId) {
      console.error(`[ERROR] Missing passenger reference ID for product group ${groupKey}`);
      return; // Skip this product group
    }
    
    // Get original product info for this passenger and direction
    const originalProductInfo = getOriginalProductInfoByPassenger(
      bookingData,
      passengerRefId,
      currentSegmentDirection as 'OUTBOUND' | 'INBOUND',
      false // This is an additional product, not a main product
    )
    
    // Get product code from original info or product data
    const productCode = originalProductInfo?.productCode || productToUse.product_code || '';
    
    // Check if the product is available on the new journey segment
    let isProductAvailable = true;
    if (newJourneyProductsByService && productCode) {
      // Create a mock product object to check availability
      const mockProduct = {
        product_code: productCode,
        service_identifier: apiRequest.segments.find((s: any) => s.ref_id === segmentRefId)?.service_identifier
      };
      
      isProductAvailable = isProductAvailableForService(mockProduct, newJourneyProductsByService);
    }
    
    // Only create and add the item if the product is available
    let additionalItem = null;
    if (isProductAvailable) {
      // Let the createRebookingItem function generate the ID
      additionalItem = createRebookingItem(true, {
        passenger_ref_id: passengerRefId,
        segment_ref_id: segmentRefId,
        product_code: productCode,
        tariff_code: originalProductInfo?.tariffCode || productToUse.tariff_code || productToUse.product_code || ''
        // Price field removed as it causes validation errors
      });
    }
    
    const additionalItemRefId = additionalItem ? additionalItem.ref_id : '';
    
    if (additionalItem) {
      apiRequest.items.push(additionalItem);
      
      const existingItemIds = products
        .filter(p => (p as any).uuid)
        .map(p => (p as any).uuid as string)
        .filter(id => !globalUsedExistingItemIds.has(id));
      
      if (existingItemIds.length > 0) {
        const validPassenger = validateSamePassenger(existingItemIds, bookingData);
        const validDirection = validateSameDirection(existingItemIds, bookingData);
        
        if (validPassenger && validDirection) {
          const itemsForThisSegment = [];
          
          for (const itemId of existingItemIds) {
            const originalItem = findOriginalItem(itemId, bookingData);
            
            if (originalItem && 
                (originalItem.passenger_ref_id === passengerRefId || 
                 originalItem.passenger_id === (productToUse as any).passenger_id || 
                 originalItem.passenger_uuid === (productToUse as any).passenger_uuid) && 
                isItemForSegment(originalItem, segmentRefId, bookingData)) {
              
              itemsForThisSegment.push(itemId);
            }
          }
          
          if (itemsForThisSegment.length > 0) {
            itemsForThisSegment.forEach(itemId => {
              apiRequest.item_adjustments.replacements.push({
                existing_item_ids: [itemId],
                added_item_ref_ids: [additionalItemRefId]
              });
              
              globalUsedExistingItemIds.add(itemId);
            });
          }

        } else {
          if (!validPassenger) {
            console.error(`[ERROR] Cannot create replacement: items belong to different passengers`);
          }
          if (!validDirection) {
            console.error(`[ERROR] Cannot create replacement: items have different directions`);
          }
          console.error(`[ERROR] Skipping replacement for items: ${existingItemIds.join(', ')}`);
        }
      }
    }
  });
}

/**
 * Prepares the rebooking request payload with enhanced journey grouping support
 * 
 * @param bookingData Original booking data
 * @param selectedTravel Selected outbound travel data
 * @param selectedReturnTravel Selected return travel data, if any
 * @param itemsToReplace List of item IDs to replace
 * @returns API request payload for rebooking
 */
export const prepareRebookingRequest = async (
  bookingData: any,
  selectedTravel: any,
  selectedReturnTravel: any | null,
  itemsToReplace: string[] = []
) => {
  // Reset the item ID counter to ensure sequential IDs start from 1
  resetItemIdCounter();
  
  // Note: The global tracking set for used item IDs will be reset after
  // the request is fully built to prevent duplicate item IDs in replacement mappings
  try {
    // Debug logs
    console.log('Preparing rebooking request with:', {
      'has selectedTravel': !!selectedTravel,
      'has selectedReturnTravel': !!selectedReturnTravel,
      'items to replace count': itemsToReplace.length,
    })

    console.log('Booking data segments:', {
      'Outbound segments':
        bookingData?.outbound_booking_tariff_segments?.length || 0,
      'Inbound segments':
        bookingData?.inbound_booking_tariff_segments?.length || 0,
    })

    if (selectedTravel) {
      console.log('Selected travel routes:', selectedTravel?.routes?.length || 0)
      console.log(
        'Selected travel first route legs:',
        selectedTravel?.routes?.[0]?.legs?.length || 0
      )
    }

    // Initialize maps and sets for tracking products
    const newJourneyProductsByService = new Map<string, Set<string>>()
    const actualBookingProducts = new Set<string>()
    const actualBookingProductUuids = new Set<string>()
    const originalBookingRequiredProductsByRef = new Map<string, any>()
    const originalBookingTariffCodesBySegment = new Map<string, Set<string>>()

    // Process outbound and inbound segments - filter to only include active segments
    const allOutboundSegments = Array.isArray(bookingData?.outbound_booking_tariff_segments) 
      ? bookingData.outbound_booking_tariff_segments 
      : [];
    const allInboundSegments = Array.isArray(bookingData?.inbound_booking_tariff_segments) 
      ? bookingData.inbound_booking_tariff_segments 
      : [];
    
    // Filter to only include active segments
    const outboundSegments = Array.isArray(allOutboundSegments) 
      ? allOutboundSegments.filter(isSegmentActive) 
      : [];
    const inboundSegments = Array.isArray(allInboundSegments) 
      ? allInboundSegments.filter(isSegmentActive) 
      : [];
    
    console.log(`[DEBUG] Filtered segments: ${outboundSegments.length}/${allOutboundSegments.length} outbound and ${inboundSegments.length}/${allInboundSegments.length} inbound segments are active`)
    
    // Identify all additional products that can be rebooked - only from active segments
    const allSegments = [
      ...outboundSegments,
      ...inboundSegments
    ]

    // Get all additional products from all segments using the utility function
    const allAdditionalProducts = getAllAdditionalProducts(allSegments);
    
    // Use the utility function to filter rebookable products based on availability
    // Pass bookingData to allow service lookup from segments for additional products
    const additionalProductsToRebook: RebookingAdditionalProduct[] = filterRebookableProducts(
      allSegments,
      newJourneyProductsByService,
      bookingData
    )
    
    // Identify products that are not available on the new service and should be cancelled
    const unavailableProducts = identifyUnavailableProducts(
      allAdditionalProducts,
      additionalProductsToRebook,
      itemsToReplace
    );
    
    // Add unavailable products to itemsToReplace
    if (unavailableProducts.length > 0) {
      itemsToReplace = addUnavailableProductsToReplace(unavailableProducts, itemsToReplace);
    }

    // Extract product information from segments with enhanced error handling
    try {
      console.log(`[DEBUG] Extracting product info from ${outboundSegments.length} outbound segments`)
      extractProductInfoFromSegments(outboundSegments, 'outbound', {
        actualBookingProducts,
        actualBookingProductUuids,
        originalBookingRequiredProductsByRef,
        originalBookingTariffCodesBySegment
      })
    } catch (error) {
      console.error('[ERROR] Failed to extract outbound product information:', error)
      // Continue processing - don't throw error here to allow inbound processing
    }

    try {
      console.log(`[DEBUG] Extracting product info from ${inboundSegments.length} inbound segments`)
      extractProductInfoFromSegments(inboundSegments, 'inbound', {
        actualBookingProducts,
        actualBookingProductUuids,
        originalBookingRequiredProductsByRef,
        originalBookingTariffCodesBySegment
      })
    } catch (error) {
      console.error('[ERROR] Failed to extract inbound product information:', error)
      // Continue processing - we've already handled outbound segments
    }

    // Extract product codes from selected travels
    extractProductCodes(selectedTravel, 'outbound', newJourneyProductsByService)
    if (selectedReturnTravel) {
      extractProductCodes(
        selectedReturnTravel,
        'inbound',
        newJourneyProductsByService
      )
    }

    // Use the interfaces defined at the module level
    
    // Initialize the API request structure
    const apiRequest = {
      currency: bookingData.currency || 'NOK',
      item_adjustments: {
        replacements: [] as ReplacementMapping[],
        cancel_item_ids: [] as string[]
      } as ItemAdjustments,
      segments: [] as Segment[],
      items: [] as Item[],
      passengers: [] as Passenger[],
      reason: 'Customer initiated rebooking'
    }

    // Add passengers from booking data
    if (bookingData.passengers && Array.isArray(bookingData.passengers) && bookingData.passengers.length > 0) {
      let passengerId: string | undefined
      bookingData.passengers.forEach((passenger: any) => {
        // Get a consistent passenger reference ID - use just the ID without the 'passenger_' prefix
        // to avoid double prefixing in item references
        const passengerRefId = String(passenger.id)

        // Make sure we're adding the passenger with all required fields
        apiRequest.passengers.push({
          uuid: passenger.uuid || '', 
          ref_id: passengerRefId,
          type: passenger.type || 'ADT'
        })

        console.log(`[DEBUG] Added passenger with ref_id: ${passengerRefId}, uuid: ${passenger.uuid}, type: ${passenger.type || 'ADT'}`)

        if (!passengerId) {
          passengerId = passengerRefId
        }
      })
    }

    const journeyIdMap = extractJourneyIdsFromBooking(bookingData);

    if (selectedTravel) {
      if (selectedTravel.routes?.[0]) {
        const outboundRoute = selectedTravel.routes[0]
        const outboundLegs = outboundRoute?.legs || []
        
        processJourneyLegs(
          outboundLegs,
          Direction.OUTBOUND,
          bookingData,
          outboundSegments,
          apiRequest,
          originalBookingTariffCodesBySegment,
          newJourneyProductsByService,
          itemsToReplace,
          selectedTravel,
          journeyIdMap
        )
      }
    }

    if (selectedReturnTravel) {
      if (selectedReturnTravel.routes?.[0]) {
        const returnRoute = selectedReturnTravel.routes[0]
        const returnLegs = returnRoute?.legs || []

        processJourneyLegs(
          returnLegs,
          Direction.INBOUND,
          bookingData,
          inboundSegments,
          apiRequest,
          originalBookingTariffCodesBySegment,
          newJourneyProductsByService,
          itemsToReplace,
          selectedReturnTravel,
          journeyIdMap
        )
      }
    }
   
    collectCancelIds(
      apiRequest,
      bookingData,
      itemsToReplace
    )
    

    processAdditionalProductsForRebooking(additionalProductsToRebook, apiRequest, itemsToReplace, newJourneyProductsByService)
    
    const outboundItems = apiRequest.items.filter((item: any) => {
      const segment = apiRequest.segments.find((s: any) => s.ref_id === item.segment_ref_id);
      return segment && segment.direction === Direction.OUTBOUND.toString();
    });
    
    const inboundItems = apiRequest.items.filter((item: any) => {
      const segment = apiRequest.segments.find((s: any) => s.ref_id === item.segment_ref_id);
      return segment && segment.direction === Direction.INBOUND.toString();
    });
    
    console.log(`[DEBUG] Items by direction: Outbound=${outboundItems.length}, Inbound=${inboundItems.length}`);
    
    // If we have outbound segments but no outbound items, this is a problem
    const outboundSegmentsInRequest = apiRequest.segments.filter((s: any) => s.direction === Direction.OUTBOUND.toString());
    if (outboundSegmentsInRequest.length > 0 && outboundItems.length === 0) {
      console.error(`[ERROR] Found ${outboundSegmentsInRequest.length} outbound segments but no outbound items!`);
      
      // Instead of adding fallback items, throw an error to make the validation failure explicit
      const errorStore = useErrorStore();
      // Use setError instead of addError to avoid potential undefined method errors
      errorStore.setError(
        `Missing outbound items in rebooking request. Found ${outboundSegmentsInRequest.length} outbound segments but no corresponding items.`
      );
      
      // Log detailed information for debugging
      console.error('[ERROR] Rebooking validation failed: Missing outbound items');
      console.error(`[ERROR] Outbound segments: ${JSON.stringify(outboundSegmentsInRequest.map(s => s.ref_id))}`);
      console.error(`[ERROR] Total items: ${apiRequest.items.length}`);
      
      // Add a fallback item for each outbound segment to ensure we have something
      console.log('[DEBUG] Adding fallback items for outbound segments');
      outboundSegmentsInRequest.forEach((segment) => {
        if (apiRequest.passengers && apiRequest.passengers.length > 0) {
          const passengerRef = apiRequest.passengers[0].ref_id;
          const fallbackItemRefId = `fallback_item_outbound_${Math.floor(Math.random() * 10000)}`;
          const fallbackProductCode = 'STANDARD'; // Use STANDARD as fallback product code
          
          const fallbackItem = {
            ref_id: fallbackItemRefId,
            passenger_ref_id: passengerRef,
            segment_ref_id: segment.ref_id,
            product_code: fallbackProductCode,
            tariff_code: fallbackProductCode
          };
          
          apiRequest.items.push(fallbackItem);
          console.log(`[DEBUG] Added fallback product ${fallbackProductCode} for segment ${segment.ref_id}`);
        }
      });
      
      // Return the request with fallback items instead of null
      return apiRequest;
    }
    
    // Debug items array structure
    console.log(`[DEBUG] Final API request items: ${apiRequest.items.length || 0} items total`)
    apiRequest.items.forEach((item: any, index: number) => {
      if (item) {
        console.log(`[DEBUG] Item ${index + 1}: ref_id=${item.ref_id}, product=${item.product_code}, segment=${item.segment_ref_id}`)
      } else {
        console.log(`[DEBUG] Item ${index + 1}: NULL/UNDEFINED item`)
      }
    })
    
    // Filter valid items and check if there are any
    const validItems = apiRequest.items.filter(item => !!item)
    if (validItems.length === 0) {
      console.error('No items in rebooking request')
      errorStore.setError('Ugyldig ombooking: Ingen produkter funnet')
      throw new Error('Invalid rebooking request: No items found')
    }
    
    // Check for items with undefined segment references and fix them
    apiRequest.items.forEach((item: any) => {
      if (!item.segment_ref_id && apiRequest.segments.length > 0) {
        console.warn(`[DEBUG] Found item ${item.ref_id} with missing segment_ref_id, fixing...`)
        item.segment_ref_id = apiRequest.segments[0].ref_id
        // Don't regenerate ref_id, keep the sequential ID format
      }
      
      // Ensure all item properties are strings (not null/undefined)
      item.ref_id = item.ref_id || ''
      item.passenger_ref_id = item.passenger_ref_id || ''
      item.segment_ref_id = item.segment_ref_id || ''
      item.product_code = item.product_code || ''
      item.tariff_code = item.tariff_code || item.product_code || ''
      
      // Remove type field if present as it's not in the working example
      delete item.type
    })
    
    // Also validate that all required segments have reference IDs
    apiRequest.segments.forEach((segment: any) => {
      // Ensure all segment properties are strings
      segment.ref_id = segment.ref_id || ''
      segment.service_identifier = segment.service_identifier || ''
      segment.origin_station_code = segment.origin_station_code || ''
      segment.destination_station_code = segment.destination_station_code || ''
      segment.service_name = segment.service_name || ''
      segment.service_type = segment.service_type || ''
      segment.tariff_group_code = segment.tariff_group_code || ''
      segment.schedule_date = segment.schedule_date || ''
      segment.travel_date = segment.travel_date || ''
      // Ensure direction is set
      segment.direction = segment.direction || ''
      // Remove the journey_ref for now - we'll set it correctly after populating journeys array
      delete segment.journey_ref
      // Remove deprecated fields
      delete segment.departure_station_code
      delete segment.arrival_station_code
      delete segment.passenger_refs
    })
    
    const errorStore = useErrorStore()
    
    // Validate that the API request has all necessary data
    if (apiRequest.segments.length === 0 || apiRequest.items.length === 0) {
      console.error('ERROR: Rebooking API request has empty segments or items')
      console.error('Segments count:', apiRequest.segments.length)
      console.error('Items count:', apiRequest.items.length)
      
      // Do not use fallbacks - instead throw a clear error explaining the problem
      if (apiRequest.segments.length === 0) {
        const errorMessage = 'Kan ikke gjennomføre rebooking: Ingen segmenter funnet i reisedata'
        errorStore.setError(errorMessage)
        throw new Error(errorMessage)
      } else if (apiRequest.items.length === 0) {
        const errorMessage = 'Kan ikke gjennomføre rebooking: Ingen produkter funnet i reisedata'
        errorStore.setError(errorMessage)
        throw new Error(errorMessage)
      }
    }
    
    // Validate that we have all necessary data in the segments
    for (const segment of apiRequest.segments) {
      if (!segment.origin_station_code || !segment.destination_station_code) {
        console.error('Missing origin or destination station code in segment', segment)
        errorStore.setError('Ugyldig ombooking: Ufullstendig reiseinformasjon')
        throw new Error('Invalid rebooking request: Missing station codes')
      }
    }
    
    // Process return journey (if selected)
    if (selectedReturnTravel) {
      console.log('Selected return route:', selectedReturnTravel?.routes?.[0] ? 'exists' : 'not available')
      if (selectedReturnTravel.routes?.[0]) {
        const returnRoute = selectedReturnTravel.routes[0]
        const returnLegs = returnRoute?.legs || []
        console.log(`Number of legs in return route: ${returnLegs.length}`)
        
        // Process return legs
        processJourneyLegs(
          returnLegs,
          Direction.INBOUND,
          bookingData,
          inboundSegments,
          apiRequest,
          originalBookingTariffCodesBySegment,
          newJourneyProductsByService,
          itemsToReplace,
          selectedReturnTravel,
          journeyIdMap
        )
      }
    }
    
    // Collect item IDs to cancel from the original booking
    apiRequest.item_adjustments.cancel_item_ids = collectCancelIds(
      apiRequest,
      bookingData,
      itemsToReplace
    )
    
    // Process additional products to rebook (e.g., seat reservations)
    if (additionalProductsToRebook && additionalProductsToRebook.length > 0) {
      addAdditionalProductsToApiRequest(
        apiRequest,
        additionalProductsToRebook,
        bookingData,
        itemsToReplace,
        newJourneyProductsByService
      )
    }
    
    for (const segment of apiRequest.segments) {
      if (!segment.origin_station_code || !segment.destination_station_code) {
        console.error('Missing origin or destination station code in segment', segment)
        errorStore.setError('Ugyldig ombooking: Ufullstendig reiseinformasjon')
        throw new Error('Invalid rebooking request: Missing station codes')
      }
    }
    
    // In the production implementation, the journeys array is not used
    // Instead, we just ensure all segments are properly configured
    console.log(`[DEBUG] Total segments in request: ${apiRequest.segments.length}`)
    console.log(`[DEBUG] Outbound segments: ${apiRequest.segments.filter((s: any) => s.direction === Direction.OUTBOUND).length}`)
    console.log(`[DEBUG] Inbound segments: ${apiRequest.segments.filter((s: any) => s.direction === Direction.INBOUND).length}`)
    
    // Remove journey_ref from segments if it exists
    console.log('[DEBUG] Removing any journey_ref from segments and items')
    
    // Create a reverse mapping from segment ref_id to original segment UUID
    const segmentRefToUuidMap: Record<string, string> = {};
    
    // Map outbound segments - only use active segments
    outboundSegments.forEach((originalSegment: any) => {
      // Only process active segments
      if (isSegmentActive(originalSegment)) {
        apiRequest.segments.forEach((segment: any) => {
          if (segment.direction === Direction.OUTBOUND && 
              segment.service_identifier === originalSegment.service_identifier &&
              segment.origin_station_code === originalSegment.departure_station_code &&
              segment.destination_station_code === originalSegment.arrival_station_code) {
            segmentRefToUuidMap[segment.ref_id] = originalSegment.uuid;
            console.log(`[DEBUG] Mapped outbound segment ${segment.ref_id} to original UUID: ${originalSegment.uuid} (journey ID: ${journeyIdMap[originalSegment.uuid] || 'unknown'})`);
          }
        });
      }
    });
    
    // Map inbound segments - only use active segments
    inboundSegments.forEach((originalSegment: any) => {
      // Only process active segments
      if (isSegmentActive(originalSegment)) {
        apiRequest.segments.forEach((segment: any) => {
          if (segment.direction === Direction.INBOUND && 
              segment.service_identifier === originalSegment.service_identifier &&
              segment.origin_station_code === originalSegment.departure_station_code &&
              segment.destination_station_code === originalSegment.arrival_station_code) {
            segmentRefToUuidMap[segment.ref_id] = originalSegment.uuid;
            console.log(`[DEBUG] Mapped inbound segment ${segment.ref_id} to original UUID: ${originalSegment.uuid} (journey ID: ${journeyIdMap[originalSegment.uuid] || 'unknown'})`);
          }
        });
      }
    });
    
    // In production implementation, we don't group segments by journey IDs
    // We just ensure all segments have the correct fields
    apiRequest.segments.forEach((segment: any) => {
      // Remove journey_ref from segment if it exists
      delete segment.journey_ref;
      
      // Ensure station codes are correctly named (origin/destination instead of departure/arrival)
      if (segment.departure_station_code && !segment.origin_station_code) {
        segment.origin_station_code = segment.departure_station_code;
        delete segment.departure_station_code;
      }
      
      if (segment.arrival_station_code && !segment.destination_station_code) {
        segment.destination_station_code = segment.arrival_station_code;
        delete segment.arrival_station_code;
      }
      
      // Log segment details for debugging
      console.log(`[DEBUG] Processed segment ${segment.ref_id}: ${segment.origin_station_code} to ${segment.destination_station_code}`);
    });
    
    // In production implementation, we don't create journey objects
    // Just log segment details for verification
    console.log('[DEBUG] Final segments in request:');
    apiRequest.segments.forEach((segment: any) => {
      console.log(`- Segment ${segment.ref_id} (${segment.direction}) from ${segment.origin_station_code} to ${segment.destination_station_code}`);
    });
    
    // Log segment counts for verification
    console.log('[DEBUG] Number of outbound segments:', apiRequest.segments.filter((s: any) => s.direction === 'outbound').length);
    console.log('[DEBUG] Number of inbound segments:', apiRequest.segments.filter((s: any) => s.direction === 'inbound').length);
    console.log('[DEBUG] Number of active outbound booking segments:', outboundSegments.length);
    console.log('[DEBUG] Number of active inbound booking segments:', inboundSegments.length);
    console.log('[DEBUG] Total outbound booking tariff segments:', bookingData.outbound_booking_tariff_segments?.length || 0);
    console.log('[DEBUG] Total inbound booking tariff segments:', bookingData.inbound_booking_tariff_segments?.length || 0);
    
    // Final validation check
    if (apiRequest.segments.length === 0) {
      errorStore.setError('Ugyldig ombooking: Ingen segmenter funnet');
      throw new Error('Invalid rebooking request: No segments found');
    }
    
    // Final validation and fixes for item adjustments
    // This is a critical part of the request structure
    if (!apiRequest.item_adjustments) {
      apiRequest.item_adjustments = {
        replacements: [] as ReplacementMapping[],
        cancel_item_ids: [] as string[]
      } as ItemAdjustments
    }
    
    // For cancel_item_ids, the API expects an array of strings, not objects
    if (!apiRequest.item_adjustments.cancel_item_ids || !Array.isArray(apiRequest.item_adjustments.cancel_item_ids)) {
      apiRequest.item_adjustments.cancel_item_ids = [] as string[]
    }
    
    // Ensure replacements is properly initialized
    if (!apiRequest.item_adjustments.replacements || !Array.isArray(apiRequest.item_adjustments.replacements)) {
      apiRequest.item_adjustments.replacements = [] as ReplacementMapping[]
    }
    
    // CRITICAL: This is resetting the item_adjustments that were previously populated
    // Log the current state before resetting
    console.log('[DEBUG] BEFORE RESET - item_adjustments:', JSON.stringify({
      cancel_item_ids_length: apiRequest.item_adjustments?.cancel_item_ids?.length || 0,
      replacements_length: apiRequest.item_adjustments?.replacements?.length || 0
    }))
    
    // Instead of completely resetting, preserve any existing data
    if (!apiRequest.item_adjustments) {
      apiRequest.item_adjustments = { 
        cancel_item_ids: [],
        replacements: []
      }
    } else {
      // Ensure the structure is correct without losing data
      apiRequest.item_adjustments.cancel_item_ids = Array.isArray(apiRequest.item_adjustments.cancel_item_ids) ? 
        apiRequest.item_adjustments.cancel_item_ids : []
      apiRequest.item_adjustments.replacements = Array.isArray(apiRequest.item_adjustments.replacements) ? 
        apiRequest.item_adjustments.replacements : []
    }
    
    console.log('[DEBUG] AFTER STRUCTURE FIX - item_adjustments:', JSON.stringify({
      cancel_item_ids_length: apiRequest.item_adjustments.cancel_item_ids.length,
      replacements_length: apiRequest.item_adjustments.replacements.length
    }))
    
    // Verify that we have both outbound and inbound segments in the request
    const outboundSegmentCount = apiRequest.segments.filter((s: any) => s.direction === Direction.OUTBOUND).length
    const inboundSegmentCount = apiRequest.segments.filter((s: any) => s.direction === Direction.INBOUND).length
    
    console.log(`[DEBUG] Request has ${outboundSegmentCount} outbound segments and ${inboundSegmentCount} inbound segments`)
    
    // Verify that we have items for both outbound and inbound segments
    const outboundItemCount = apiRequest.items.filter((item: any) => {
      const segment = apiRequest.segments.find((s: any) => s.ref_id === item.segment_ref_id)
      return segment && segment.direction === Direction.OUTBOUND
    }).length
    
    const inboundItemCount = apiRequest.items.filter((item: any) => {
      const segment = apiRequest.segments.find((s: any) => s.ref_id === item.segment_ref_id)
      return segment && segment.direction === Direction.INBOUND
    }).length
    
    console.log(`[DEBUG] Request has ${outboundItemCount} outbound items and ${inboundItemCount} inbound items`)
    
    // If we have items to replace and new items, set up the replacements properly
    if (itemsToReplace && itemsToReplace.length > 0 && apiRequest.items.length > 0) {
      console.log(`[DEBUG] Processing ${itemsToReplace.length} items to replace:`, itemsToReplace)
      
      // Convert each item ID to a string
      const formattedItemIds = itemsToReplace.map(itemId => String(itemId))
      
      console.log('[DEBUG] Formatted item IDs:', formattedItemIds)
      
      // Create a map to track which original items belong to which segments
      const originalItemsToSegmentMap = new Map<string, string>()
      
      // Process outbound segments
      for (const segment of bookingData.outbound_booking_tariff_segments || []) {
        const { departureCode, arrivalCode } = extractStationCodesFromSegment(segment);
        
        // Log the extracted station codes for debugging
        console.log(`[DEBUG] Extracted outbound station codes: ${departureCode} -> ${arrivalCode} for segment ${segment.id || 'unknown'}`);
        
        for (const product of Array.isArray(segment.required_products) ? segment.required_products : []) {
          if (formattedItemIds.includes(product.uuid)) {
            originalItemsToSegmentMap.set(product.uuid, `outbound_${departureCode}_${arrivalCode}`)
          }
        }
      }
      
      // Process inbound segments
      for (const segment of bookingData.inbound_booking_tariff_segments || []) {
        const { departureCode, arrivalCode } = extractStationCodesFromSegment(segment);
        
        // Log the extracted station codes for debugging
        console.log(`[DEBUG] Extracted inbound station codes: ${departureCode} -> ${arrivalCode} for segment ${segment.id || 'unknown'}`);
        
        for (const product of Array.isArray(segment.required_products) ? segment.required_products : []) {
          if (formattedItemIds.includes(product.uuid)) {
            originalItemsToSegmentMap.set(product.uuid, `inbound_${departureCode}_${arrivalCode}`)
          }
        }
      }
      
      // Log the original items and their segments
      console.log('[DEBUG] Original items mapped to segments:')
      originalItemsToSegmentMap.forEach((segmentKey, itemId) => {
        console.log(`[DEBUG] Item ${itemId} belongs to segment ${segmentKey}`)
      })
      
      // Map new segments to their original segments
      const newToOriginalSegmentMap = new Map<string, string>()
      
      apiRequest.segments.forEach((segment: any) => {
        // Create a key for this new segment using the correct properties
        const newSegmentKey = `${segment.direction === Direction.OUTBOUND ? 'outbound' : 'inbound'}_${segment.departure_station_code}_${segment.arrival_station_code}`
        
        // Find the best matching original segment key
        let bestMatch = ''
        originalItemsToSegmentMap.forEach((originalSegmentKey) => {
          if (originalSegmentKey.startsWith(segment.direction === Direction.OUTBOUND ? 'outbound' : 'inbound')) {
            // Simple matching for now - can be enhanced with more sophisticated logic
            if (!bestMatch || originalSegmentKey.includes(segment.departure_station_code) || originalSegmentKey.includes(segment.arrival_station_code)) {
              bestMatch = originalSegmentKey
            }
          }
        })
        
        if (bestMatch) {
          newToOriginalSegmentMap.set(segment.ref_id, bestMatch)
          console.log(`[DEBUG] Mapped new segment ${segment.ref_id} to original segment key ${bestMatch}`)
        }
      })
      
      // Create more granular replacement mappings based on segments
      const processedItems = new Set<string>()
      
      // Group items by original segment
      const itemsByOriginalSegment = new Map<string, string[]>()
      
      originalItemsToSegmentMap.forEach((segmentKey, itemId) => {
        if (!itemsByOriginalSegment.has(segmentKey)) {
          itemsByOriginalSegment.set(segmentKey, [])
        }
        itemsByOriginalSegment.get(segmentKey)?.push(itemId)
      })
      
      // Track all items that have been used in segment-specific mappings
      // Declare this at a higher scope level so it can be used later for filtering
      const usedItemRefIds = new Set<string>()
      
      // Group items by passenger and direction
      // First, create mappings for item UUIDs to passenger IDs and directions
      const originalItemToPassengerMap = new Map<string, string>()
      const originalItemToDirectionMap = new Map<string, string>()
      
      // Create a mapping from passenger_id to passenger UUID
      const passengerIdToUuidMap = new Map<string, string>()
      bookingData.passengers.forEach((passenger: any) => {
        if (passenger.id && passenger.uuid) {
          passengerIdToUuidMap.set(passenger.id, passenger.uuid)
        }
      })
      
      // Process outbound segments and map items to passengers and direction
      const outboundSegments = Array.isArray(bookingData.outbound_booking_tariff_segments) ? bookingData.outbound_booking_tariff_segments : []
      
      // Log the structure of outbound segments for debugging
      console.log(`[DEBUG] Processing ${outboundSegments.length} outbound segments`)
      
      // Safely iterate over outbound segments
      if (Array.isArray(outboundSegments)) {
        outboundSegments.forEach((segment, segmentIndex) => {
          try {
            if (!segment) {
              console.log(`[WARNING] Skipping null/undefined outbound segment at index ${segmentIndex}`)
              return
            }
            
            console.log(`[DEBUG] Processing outbound segment ${segmentIndex}: ${segment.id || 'unknown'}`)
            
            // Map required products with enhanced error handling
            try {
              const requiredProducts = Array.isArray(segment.required_products) ? segment.required_products : []
              console.log(`[DEBUG] Found ${requiredProducts.length} required products in outbound segment ${segmentIndex}`)
              
              requiredProducts.forEach((product: any, productIndex: number) => {
                try {
                  if (!product) {
                    console.log(`[WARNING] Skipping null/undefined required product at index ${productIndex} in outbound segment ${segmentIndex}`)
                    return
                  }
                  
                  if (product.uuid && product.passenger_id) {
                    const passengerUuid = passengerIdToUuidMap.get(product.passenger_id)
                    if (passengerUuid) {
                      originalItemToPassengerMap.set(product.uuid, passengerUuid)
                      originalItemToDirectionMap.set(product.uuid, Direction.OUTBOUND)
                      console.log(`[DEBUG] Mapped required product ${product.uuid} to passenger ${passengerUuid} (outbound)`)
                    } else {
                      console.log(`[WARNING] No passenger UUID found for passenger ID ${product.passenger_id} in required product ${product.uuid}`)
                    }
                  } else {
                    console.log(`[WARNING] Missing uuid or passenger_id in required product at index ${productIndex} in outbound segment ${segmentIndex}`)
                  }
                } catch (productError) {
                  console.error(`[ERROR] Failed to process required product at index ${productIndex} in outbound segment ${segmentIndex}:`, productError)
                }
              })
            } catch (requiredProductsError) {
              console.error(`[ERROR] Failed to process required products for outbound segment ${segmentIndex}:`, requiredProductsError)
            }
            
            // Map additional products with enhanced error handling
            try {
              // Check if additional_products exists and is an array
              if (segment.additional_products === undefined || segment.additional_products === null) {
                console.log(`[DEBUG] No additional_products found in outbound segment ${segmentIndex}`)
              } else if (!Array.isArray(segment.additional_products)) {
                console.log(`[WARNING] additional_products is not an array in outbound segment ${segmentIndex}. Type: ${typeof segment.additional_products}`)
                console.log(`[DEBUG] Value of additional_products:`, segment.additional_products)
              } else {
                console.log(`[DEBUG] Found ${segment.additional_products.length} additional products in outbound segment ${segmentIndex}`)
                
                segment.additional_products.forEach((product: any, productIndex: number) => {
                  try {
                    if (!product) {
                      console.log(`[WARNING] Skipping null/undefined additional product at index ${productIndex} in outbound segment ${segmentIndex}`)
                      return
                    }
                    
                    if (product.uuid && product.passenger_id) {
                      const passengerUuid = passengerIdToUuidMap.get(product.passenger_id)
                      if (passengerUuid) {
                        originalItemToPassengerMap.set(product.uuid, passengerUuid)
                        originalItemToDirectionMap.set(product.uuid, Direction.OUTBOUND)
                        console.log(`[DEBUG] Mapped additional product ${product.uuid} to passenger ${passengerUuid} (outbound)`)
                      } else {
                        console.log(`[WARNING] No passenger UUID found for passenger ID ${product.passenger_id} in additional product ${product.uuid}`)
                      }
                    } else {
                      console.log(`[WARNING] Missing uuid or passenger_id in additional product at index ${productIndex} in outbound segment ${segmentIndex}`)
                    }
                  } catch (productError) {
                    console.error(`[ERROR] Failed to process additional product at index ${productIndex} in outbound segment ${segmentIndex}:`, productError)
                  }
                })
              }
            } catch (additionalProductsError) {
              console.error(`[ERROR] Failed to process additional products for outbound segment ${segmentIndex}:`, additionalProductsError)
            }
          } catch (segmentError) {
            console.error(`[ERROR] Failed to process outbound segment at index ${segmentIndex}:`, segmentError)
          }
        })
      } else {
        console.log(`[WARNING] outboundSegments is not an array. Type: ${typeof outboundSegments}`)
      }
      
      // Process inbound segments and map items to passengers and direction
      const inboundSegments = Array.isArray(bookingData.inbound_booking_tariff_segments) ? bookingData.inbound_booking_tariff_segments : []
      
      // Log the structure of inbound segments for debugging
      console.log(`[DEBUG] Processing ${inboundSegments.length} inbound segments`)
      
      // Safely iterate over inbound segments
      if (Array.isArray(inboundSegments)) {
        inboundSegments.forEach((segment, segmentIndex) => {
          try {
            if (!segment) {
              console.log(`[WARNING] Skipping null/undefined inbound segment at index ${segmentIndex}`)
              return
            }
            
            console.log(`[DEBUG] Processing inbound segment ${segmentIndex}: ${segment.id || 'unknown'}`)
            
            // Map required products with enhanced error handling
            try {
              const requiredProducts = Array.isArray(segment.required_products) ? segment.required_products : []
              console.log(`[DEBUG] Found ${requiredProducts.length} required products in inbound segment ${segmentIndex}`)
              
              requiredProducts.forEach((product: any, productIndex: number) => {
                try {
                  if (!product) {
                    console.log(`[WARNING] Skipping null/undefined required product at index ${productIndex} in inbound segment ${segmentIndex}`)
                    return
                  }
                  
                  if (product.uuid && product.passenger_id) {
                    const passengerUuid = passengerIdToUuidMap.get(product.passenger_id)
                    if (passengerUuid) {
                      originalItemToPassengerMap.set(product.uuid, passengerUuid)
                      originalItemToDirectionMap.set(product.uuid, Direction.INBOUND)
                      console.log(`[DEBUG] Mapped required product ${product.uuid} to passenger ${passengerUuid} (inbound)`)
                    } else {
                      console.log(`[WARNING] No passenger UUID found for passenger ID ${product.passenger_id} in required product ${product.uuid}`)
                    }
                  } else {
                    console.log(`[WARNING] Missing uuid or passenger_id in required product at index ${productIndex} in inbound segment ${segmentIndex}`)
                  }
                } catch (productError) {
                  console.error(`[ERROR] Failed to process required product at index ${productIndex} in inbound segment ${segmentIndex}:`, productError)
                }
              })
            } catch (requiredProductsError) {
              console.error(`[ERROR] Failed to process required products for inbound segment ${segmentIndex}:`, requiredProductsError)
            }
            
            // Map additional products with enhanced error handling
            try {
              // Check if additional_products exists and is an array
              if (segment.additional_products === undefined || segment.additional_products === null) {
                console.log(`[DEBUG] No additional_products found in inbound segment ${segmentIndex}`)
              } else if (!Array.isArray(segment.additional_products)) {
                console.log(`[WARNING] additional_products is not an array in inbound segment ${segmentIndex}. Type: ${typeof segment.additional_products}`)
                console.log(`[DEBUG] Value of additional_products:`, segment.additional_products)
              } else {
                console.log(`[DEBUG] Found ${segment.additional_products.length} additional products in inbound segment ${segmentIndex}`)
                
                segment.additional_products.forEach((product: any, productIndex: number) => {
                  try {
                    if (!product) {
                      console.log(`[WARNING] Skipping null/undefined additional product at index ${productIndex} in inbound segment ${segmentIndex}`)
                      return
                    }
                    
                    if (product.uuid && product.passenger_id) {
                      const passengerUuid = passengerIdToUuidMap.get(product.passenger_id)
                      if (passengerUuid) {
                        originalItemToPassengerMap.set(product.uuid, passengerUuid)
                        originalItemToDirectionMap.set(product.uuid, Direction.INBOUND)
                        console.log(`[DEBUG] Mapped additional product ${product.uuid} to passenger ${passengerUuid} (inbound)`)
                      } else {
                        console.log(`[WARNING] No passenger UUID found for passenger ID ${product.passenger_id} in additional product ${product.uuid}`)
                      }
                    } else {
                      console.log(`[WARNING] Missing uuid or passenger_id in additional product at index ${productIndex} in inbound segment ${segmentIndex}`)
                    }
                  } catch (productError) {
                    console.error(`[ERROR] Failed to process additional product at index ${productIndex} in inbound segment ${segmentIndex}:`, productError)
                  }
                })
              }
            } catch (additionalProductsError) {
              console.error(`[ERROR] Failed to process additional products for inbound segment ${segmentIndex}:`, additionalProductsError)
            }
          } catch (segmentError) {
            console.error(`[ERROR] Failed to process inbound segment at index ${segmentIndex}:`, segmentError)
          }
        })
      } else {
        console.log(`[WARNING] inboundSegments is not an array. Type: ${typeof inboundSegments}`)
      }
      
      // Also map new items to their passenger ref IDs
      const newItemToPassengerMap = new Map<string, string>()
      apiRequest.items.forEach((item: any) => {
        if (item.ref_id && item.passenger_ref_id) {
          newItemToPassengerMap.set(item.ref_id, item.passenger_ref_id)
        }
      })
      
      // Map passenger UUIDs to their ref_ids
      const passengerUuidToRefMap = new Map<string, string>()
      apiRequest.passengers.forEach((passenger: any) => {
        if (passenger.uuid && passenger.ref_id) {
          passengerUuidToRefMap.set(passenger.uuid, passenger.ref_id)
        }
      })
      
      // Group items by passenger AND direction
      // Key format: "passengerRefId:direction"
      const itemsByPassengerAndDirection = new Map<string, { originalItems: string[], newItems: string[] }>()
      
      // Initialize the map with all passenger and direction combinations
      apiRequest.passengers.forEach((passenger: any) => {
        if (passenger.ref_id) {
          // Create entry for outbound direction
          itemsByPassengerAndDirection.set(
            `${passenger.ref_id}:${Direction.OUTBOUND}`, 
            { originalItems: [], newItems: [] }
          )
          
          // Create entry for inbound direction
          itemsByPassengerAndDirection.set(
            `${passenger.ref_id}:${Direction.INBOUND}`, 
            { originalItems: [], newItems: [] }
          )
        }
      })
      
      // Map new items to their directions
      const newItemToDirectionMap = new Map<string, string>()
      apiRequest.items.forEach((item: any) => {
        if (item.ref_id && item.segment_ref_id) {
          // Find the segment to determine direction
          const segment = apiRequest.segments.find((s: any) => s.ref_id === item.segment_ref_id)
          if (segment && segment.direction) {
            newItemToDirectionMap.set(item.ref_id, segment.direction)
          }
        }
      })
      
      // Group original items by passenger and direction
      formattedItemIds.forEach(itemId => {
        const passengerUuid = originalItemToPassengerMap.get(itemId)
        const direction = originalItemToDirectionMap.get(itemId)
        
        if (passengerUuid && direction) {
          const passengerRefId = passengerUuidToRefMap.get(passengerUuid)
          if (passengerRefId) {
            const key = `${passengerRefId}:${direction}`
            if (itemsByPassengerAndDirection.has(key)) {
              itemsByPassengerAndDirection.get(key)?.originalItems.push(itemId)
            }
          } else {
            console.warn(`[WARN] Could not find passenger ref ID for UUID ${passengerUuid}`)
          }
        } else {
          console.warn(`[WARN] Could not find passenger or direction for item ${itemId}`)
        }
      })
      
      // Group new items by passenger and direction
      apiRequest.items.forEach((item: any) => {
        if (item.ref_id && item.passenger_ref_id) {
          const direction = newItemToDirectionMap.get(item.ref_id)
          if (direction) {
            const key = `${item.passenger_ref_id}:${direction}`
            if (itemsByPassengerAndDirection.has(key)) {
              itemsByPassengerAndDirection.get(key)?.newItems.push(item.ref_id)
            }
          } else {
            console.warn(`[WARN] Could not determine direction for new item ${item.ref_id}`)
          }
        } else {
          console.warn(`[WARN] Item missing ref_id or passenger_ref_id: ${JSON.stringify(item)}`)
        }
      })
      
      // Create replacement mappings by passenger and direction, but separate by product type
      itemsByPassengerAndDirection.forEach((items, key) => {
        const { originalItems, newItems } = items
        const [passengerRefId, direction] = key.split(':')
        
        if (originalItems.length > 0 && newItems.length > 0) {
          // Separate original items into main products and add-ons
          const originalMainProducts: string[] = []
          const originalAddons: string[] = []
          
          // Classify original items as main products or add-ons
          originalItems.forEach(itemId => {
            const originalItem = findOriginalItem(itemId, bookingData)
            // ST represents seat and ticket (main product)
            const isMainProduct = originalItem?.product_type === 'ST' || 
                                 originalItem?.type === 'ST' || 
                                 ['STANDARD', 'FLEX', 'MINIPRIS'].includes(originalItem?.product_code || '')
            
            if (isMainProduct) {
              originalMainProducts.push(itemId)
              console.log(`[DEBUG] Classified ${itemId} as main product with code ${originalItem?.product_code}`)
            } else {
              originalAddons.push(itemId)
              console.log(`[DEBUG] Classified ${itemId} as add-on with code ${originalItem?.product_code}`)
            }
          })
          
          // Separate new items into main products and add-ons
          const newMainProducts: string[] = []
          const newAddons: string[] = []
          
          // Classify new items as main products or add-ons
          newItems.forEach(itemRefId => {
            const newItem = apiRequest.items.find((item: any) => item.ref_id === itemRefId)
            // Main products are typically STANDARD, FLEX, or MINIPRIS
            const isMainProduct = ['STANDARD', 'FLEX', 'MINIPRIS'].includes(newItem?.product_code || '')
            
            if (isMainProduct) {
              newMainProducts.push(itemRefId)
              console.log(`[DEBUG] Classified new item ${itemRefId} as main product with code ${newItem?.product_code}`)
            } else {
              newAddons.push(itemRefId)
              console.log(`[DEBUG] Classified new item ${itemRefId} as add-on with code ${newItem?.product_code}`)
            }
          })
          
          // Create separate replacement mappings for main products and add-ons
          if (originalMainProducts.length > 0 && newMainProducts.length > 0) {
            // Validate that all original main products belong to the same passenger
            const passengerIds = new Set<string>();
            originalMainProducts.forEach(itemId => {
              const passengerUuid = originalItemToPassengerMap.get(itemId);
              if (passengerUuid) {
                passengerIds.add(passengerUuid);
              }
            });
            
            // Only create mapping if all items belong to the same passenger
            if (passengerIds.size === 1) {
              // Check if any of the original items are already used in a replacement mapping
              const unusedOriginalItems = originalMainProducts.filter(id => !globalUsedExistingItemIds.has(id));
              
              if (unusedOriginalItems.length > 0) {
                apiRequest.item_adjustments.replacements.push({
                  existing_item_ids: unusedOriginalItems,
                  added_item_ref_ids: newMainProducts
                } as ReplacementMapping)
                
                // Mark these items as processed in both local and global tracking
                unusedOriginalItems.forEach(id => {
                  processedItems.add(id);
                  globalUsedExistingItemIds.add(id);
                  console.log(`[DEBUG] Marked main product ${id} as used in global tracking set`);
                })
                newMainProducts.forEach(id => usedItemRefIds.add(id))
              } else {
                console.log(`[DEBUG] All main products are already used in replacement mappings, skipping`);
              }
              
              console.log(`[DEBUG] Added main product replacement mapping for passenger ${passengerRefId}, direction ${direction}:`, {
                existing_item_ids: originalMainProducts,
                added_item_ref_ids: newMainProducts
              })
            } else {
              console.warn(`[WARN] Cannot create main product replacement mapping: items belong to ${passengerIds.size} different passengers`)
            }
          }
          
          // Create separate replacement mappings for add-ons
          if (originalAddons.length > 0 && newAddons.length > 0) {
            // Validate that all original add-ons belong to the same passenger
            const passengerIds = new Set<string>();
            originalAddons.forEach(itemId => {
              const passengerUuid = originalItemToPassengerMap.get(itemId);
              if (passengerUuid) {
                passengerIds.add(passengerUuid);
              }
            });
            
            // Only create mapping if all items belong to the same passenger
            if (passengerIds.size === 1) {
              // Check if any of the original add-ons are already used in a replacement mapping
              const unusedOriginalAddons = originalAddons.filter(id => !globalUsedExistingItemIds.has(id));
              
              if (unusedOriginalAddons.length > 0) {
                apiRequest.item_adjustments.replacements.push({
                  existing_item_ids: unusedOriginalAddons,
                  added_item_ref_ids: newAddons
                } as ReplacementMapping)
                
                // Mark these items as processed in both local and global tracking
                unusedOriginalAddons.forEach(id => {
                  processedItems.add(id);
                  globalUsedExistingItemIds.add(id);
                  console.log(`[DEBUG] Marked add-on ${id} as used in global tracking set`);
                })
                newAddons.forEach(id => usedItemRefIds.add(id))
              } else {
                console.log(`[DEBUG] All add-ons are already used in replacement mappings, skipping`);
              }
              
              console.log(`[DEBUG] Added add-on replacement mapping for passenger ${passengerRefId}, direction ${direction}:`, {
                existing_item_ids: originalAddons,
                added_item_ref_ids: newAddons
              })
            } else {
              console.warn(`[WARN] Cannot create add-on replacement mapping: items belong to ${passengerIds.size} different passengers`)
            }
          }
        }
      })
      
      // Handle any remaining items that weren't mapped
      const remainingItems = formattedItemIds.filter(id => !processedItems.has(id))
      
      // If there are remaining items, try to map them by passenger and direction one more time
      if (remainingItems.length > 0) {
        // Try to group by passenger and direction one more time
        const remainingByPassengerAndDirection = new Map<string, { originalItems: string[], newItems: string[] }>()
        
        // Initialize with all passenger and direction combinations
        apiRequest.passengers.forEach((passenger: any) => {
          if (passenger.ref_id) {
            remainingByPassengerAndDirection.set(
              `${passenger.ref_id}:${Direction.OUTBOUND}`, 
              { originalItems: [], newItems: [] }
            )
            remainingByPassengerAndDirection.set(
              `${passenger.ref_id}:${Direction.INBOUND}`, 
              { originalItems: [], newItems: [] }
            )
          }
        })
        
        // Group remaining original items by passenger and direction
        remainingItems.forEach(itemId => {
          const passengerUuid = originalItemToPassengerMap.get(itemId)
          const direction = originalItemToDirectionMap.get(itemId)
          
          if (passengerUuid && direction) {
            const passengerRefId = passengerUuidToRefMap.get(passengerUuid)
            if (passengerRefId) {
              const key = `${passengerRefId}:${direction}`
              if (remainingByPassengerAndDirection.has(key)) {
                remainingByPassengerAndDirection.get(key)?.originalItems.push(itemId)
              }
            }
          }
        })
        
        // Find remaining new items
        const remainingNewItems = apiRequest.items
          .filter((item: any) => item.ref_id && !usedItemRefIds.has(item.ref_id))
        
        // Group remaining new items by passenger and direction
        remainingNewItems.forEach((item: any) => {
          if (item.ref_id && item.passenger_ref_id) {
            const direction = newItemToDirectionMap.get(item.ref_id)
            if (direction) {
              const key = `${item.passenger_ref_id}:${direction}`
              if (remainingByPassengerAndDirection.has(key)) {
                remainingByPassengerAndDirection.get(key)?.newItems.push(item.ref_id)
              }
            }
          }
        })
        
        // Create replacement mappings for remaining items by passenger and direction
        // but separate by product type
        let anyMappingsCreated = false
        remainingByPassengerAndDirection.forEach((items, key) => {
          const { originalItems, newItems } = items
          const [passengerRefId, direction] = key.split(':')
          
          if (originalItems.length > 0 && newItems.length > 0) {
            // Separate original items into main products and add-ons
            const originalMainProducts: string[] = []
            const originalAddons: string[] = []
            
            // Classify original items as main products or add-ons
            originalItems.forEach(itemId => {
              const originalItem = findOriginalItem(itemId, bookingData)
              // ST represents seat and ticket (main product)
              const isMainProduct = originalItem?.product_type === 'ST' || 
                                   originalItem?.type === 'ST' || 
                                   ['STANDARD', 'FLEX', 'MINIPRIS'].includes(originalItem?.product_code || '')
              
              if (isMainProduct) {
                originalMainProducts.push(itemId)
              } else {
                originalAddons.push(itemId)
              }
            })
            
            // Separate new items into main products and add-ons
            const newMainProducts: string[] = []
            const newAddons: string[] = []
            
            // Classify new items as main products or add-ons
            newItems.forEach(itemRefId => {
              const newItem = apiRequest.items.find((item: any) => item.ref_id === itemRefId)
              // Main products are typically STANDARD, FLEX, or MINIPRIS
              const isMainProduct = ['STANDARD', 'FLEX', 'MINIPRIS'].includes(newItem?.product_code || '')
              
              if (isMainProduct) {
                newMainProducts.push(itemRefId)
              } else {
                newAddons.push(itemRefId)
              }
            })
            
            // Create separate replacement mappings for main products and add-ons
            if (originalMainProducts.length > 0 && newMainProducts.length > 0) {
              // Filter out items that are already used in a replacement mapping
              const unusedOriginalMainProducts = originalMainProducts.filter(id => !globalUsedExistingItemIds.has(id));
              
              if (unusedOriginalMainProducts.length > 0) {
                apiRequest.item_adjustments.replacements.push({
                  existing_item_ids: unusedOriginalMainProducts,
                  added_item_ref_ids: newMainProducts
                })
                
                // Mark these items as processed in both local and global tracking
                unusedOriginalMainProducts.forEach(id => {
                  processedItems.add(id);
                  globalUsedExistingItemIds.add(id);
                  console.log(`[DEBUG] Marked fallback main product ${id} as used in global tracking set`);
                })
                newMainProducts.forEach(id => usedItemRefIds.add(id))
                
                anyMappingsCreated = true
                console.log(`[DEBUG] Added fallback main product mapping for passenger ${passengerRefId}, direction ${direction}:`, {
                  existing_item_ids: unusedOriginalMainProducts,
                  added_item_ref_ids: newMainProducts
                })
              } else {
                console.log(`[DEBUG] All fallback main products are already used in replacement mappings, skipping`);
              }
            }
            
            // Create separate replacement mappings for add-ons
            if (originalAddons.length > 0 && newAddons.length > 0) {
              // Filter out items that are already used in a replacement mapping
              const unusedOriginalAddons = originalAddons.filter(id => !globalUsedExistingItemIds.has(id));
              
              if (unusedOriginalAddons.length > 0) {
                apiRequest.item_adjustments.replacements.push({
                  existing_item_ids: unusedOriginalAddons,
                  added_item_ref_ids: newAddons
                })
                
                // Mark these items as processed in both local and global tracking
                unusedOriginalAddons.forEach(id => {
                  processedItems.add(id);
                  globalUsedExistingItemIds.add(id);
                  console.log(`[DEBUG] Marked fallback add-on ${id} as used in global tracking set`);
                })
                newAddons.forEach(id => usedItemRefIds.add(id))
                
                anyMappingsCreated = true
                console.log(`[DEBUG] Added fallback add-on mapping for passenger ${passengerRefId}, direction ${direction}:`, {
                  existing_item_ids: unusedOriginalAddons,
                  added_item_ref_ids: newAddons
                })
              } else {
                console.log(`[DEBUG] All fallback add-ons are already used in replacement mappings, skipping`);
              }
            }
          }
        })
        
        // If we still have unprocessed items, log an error but DO NOT create a fallback mapping
        // that would mix passengers or directions as this will cause API validation errors
        if (!anyMappingsCreated) {
          const finalRemainingItems = formattedItemIds.filter(id => !processedItems.has(id))
          const finalRemainingNewItems = apiRequest.items
            .filter((item: any) => item.ref_id && !usedItemRefIds.has(item.ref_id))
            .map((item: any) => item.ref_id)
          
          if (finalRemainingItems.length > 0 || finalRemainingNewItems.length > 0) {
            console.error('[ERROR] Could not create valid replacement mappings for some items')
            console.error(`Unmapped original items: ${finalRemainingItems.length}, unmapped new items: ${finalRemainingNewItems.length}`)
            console.error('These items will not be included in the rebooking request')
          }
        }
      }
      
      console.log('[DEBUG] Remaining unprocessed items:', formattedItemIds.filter(id => !processedItems.has(id)).length)
      
      // If no replacements were added but we had items to replace, log a warning
      if (apiRequest.item_adjustments.replacements.length === 0) {
        console.warn(`[WARN] No replacements could be created despite having ${itemsToReplace.length} items to replace`)
        console.warn('[WARN] No fallback replacement mapping will be created as this could violate segment boundaries')
        console.warn('[WARN] The API requires explicit and valid replacement mappings that respect segment boundaries')
        console.warn('[WARN] The request may fail if replacements are required but none could be created')
      }
      
      // Ensure that items in itemsToReplace that couldn't be mapped to replacements
      // remain in cancel_item_ids so they are properly cancelled
      // This is especially important for additional products like COMFORT_SEAT
      // that are unavailable on the new journey
      
      // Find all items that are in itemsToReplace but not in any replacement mapping
      if (Array.isArray(itemsToReplace) && itemsToReplace.length > 0) {
        // Get all items that are already in replacement mappings
        const replacedItemIds = new Set<string>();
        if (apiRequest?.item_adjustments?.replacements && Array.isArray(apiRequest.item_adjustments.replacements)) {
          apiRequest.item_adjustments.replacements.forEach(replacement => {
            if (replacement?.existing_item_ids && Array.isArray(replacement.existing_item_ids)) {
              replacement.existing_item_ids.forEach(id => replacedItemIds.add(String(id)));
            }
          });
        }
        
        // Find items that should be cancelled (in itemsToReplace but not in any replacement)
        const itemsToCancel = itemsToReplace.filter(itemId => !replacedItemIds.has(String(itemId)));
        
        // Add these items to cancel_item_ids if they're not already there
        if (itemsToCancel.length > 0) {
          console.log(`[DEBUG] Found ${itemsToCancel.length} items that need to be cancelled explicitly:`, itemsToCancel);
          
          // Initialize cancel_item_ids if needed
          if (!apiRequest.item_adjustments.cancel_item_ids) {
            apiRequest.item_adjustments.cancel_item_ids = [];
          }
          
          // Add each item to cancel_item_ids if not already present
          itemsToCancel.forEach(itemId => {
            if (!apiRequest.item_adjustments.cancel_item_ids.includes(String(itemId))) {
              apiRequest.item_adjustments.cancel_item_ids.push(String(itemId));
              console.log(`[DEBUG] Added item ${itemId} to cancel_item_ids because it has no replacement`);
            }
          });
        }
      }
    }
    
    // Add debug log for item_adjustments
    console.log('[DEBUG] Final item_adjustments:', JSON.stringify(apiRequest.item_adjustments))
    
    // Log the journey ID map and segment mapping for debugging
    console.log('[DEBUG] Original journey ID map:', journeyIdMap);
    console.log('[DEBUG] Segment ref to UUID map:', segmentRefToUuidMap);
    
    // Remove journey_ref from all items
    if (apiRequest.items) {
      apiRequest.items.forEach((item: any) => {
        delete item.journey_ref;
      });
    }
    
    // Log segment structure for debugging
    console.log(`[DEBUG] Created API request with ${apiRequest.segments.length} total segments`);
    
    // Log the final replacement mappings for debugging
    if (apiRequest.item_adjustments && apiRequest.item_adjustments.replacements) {
      console.log(`[DEBUG] Final replacement mappings: ${apiRequest.item_adjustments.replacements.length} mappings created`);
      
      // Log each replacement mapping
      apiRequest.item_adjustments.replacements.forEach((replacement: any, index: number) => {
        console.log(`[DEBUG] Replacement #${index + 1}:`);
        console.log(`  - Existing items: ${replacement.existing_item_ids.join(', ')}`);
        console.log(`  - Added items: ${replacement.added_item_ref_ids.join(', ')}`);
        
        // Validate each mapping
        const validPassenger = validateSamePassenger(replacement.existing_item_ids, bookingData);
        const validDirection = validateSameDirection(replacement.existing_item_ids, bookingData);
        
        if (!validPassenger || !validDirection) {
          console.error(`[ERROR] Invalid replacement mapping #${index + 1}:`);
          if (!validPassenger) console.error(`  - Items belong to different passengers`);
          if (!validDirection) console.error(`  - Items have different directions`);
        }
      });
    }
    
    console.log('Final API request:', {
      segmentsCount: apiRequest.segments.length,
      itemsCount: apiRequest.items.length,
      replacementsCount: apiRequest.item_adjustments.replacements.length,
      cancelItemsCount: apiRequest.item_adjustments.cancel_item_ids.length
    })
    
    // Reset the global tracking set AFTER the request is fully built
    // This ensures it's only reset between separate API requests, not during the building process
    // The next time prepareRebookingRequest is called, it will start with a clean state
    console.log('[DEBUG] Request fully built - now safe to reset global tracking for used item IDs');
    globalUsedExistingItemIds.clear();
    
    return apiRequest
  } catch (error) {
    console.error('Error preparing rebooking request:', error)
    try {
      const errorStore = useErrorStore()
      errorStore.setError('Feil ved forberedelse av endring')
    } catch (storeError) {
      console.error('Failed to set error in error store:', storeError)
    }
    throw error
  }
}

// Add the additional products to the API request
const addAdditionalProductsToApiRequest = (
  apiRequest: any, 
  additionalProducts: RebookingAdditionalProduct[], 
  bookingData: any,
  itemsToReplace: string[] = [],
  newJourneyProductsByService?: Map<string, Set<string>>
) => {
  // Implementation comes here
  if (Array.isArray(additionalProducts) && additionalProducts.length > 0) {
    console.log(`[DEBUG] Processing ${additionalProducts.length} additional products for segments`)
    
    // First, find all original add-ons in the booking that need to be cancelled
    const originalAddons = findOriginalAddons(bookingData);
   
    apiRequest.segments.forEach((segment: any) => {
      const segmentRefId = segment.ref_id;
      if (segmentRefId) {
        // Filter additional products for this segment
        const segmentProducts = additionalProducts.filter(product => {

          
          return (product.departureStation === segment.origin_station_code || 
                  product.departureStation === segment.departure_station_code) && 
                 (product.arrivalStation === segment.destination_station_code || 
                  product.arrivalStation === segment.arrival_station_code);
        });
        
        if (segmentProducts.length > 0) {
          processAdditionalProducts(segmentProducts, segmentRefId, apiRequest, itemsToReplace, bookingData, null, newJourneyProductsByService);
        }
      }
    });
    
    // Handle any original add-ons that weren't explicitly replaced
    // This ensures all original add-ons are properly cancelled during rebooking
    const replacedAddonIds = new Set();
    apiRequest.item_adjustments.replacements.forEach((replacement: any) => {
      replacement.existing_item_ids.forEach((id: string) => replacedAddonIds.add(id));
    });
    
    const unreplacedAddons = originalAddons.filter(addon => !replacedAddonIds.has(addon.uuid));
    if (unreplacedAddons.length > 0) {
      console.log(`[DEBUG] Found ${unreplacedAddons.length} unreplaced add-ons that need to be cancelled`);
      
      // Add these to the cancel_item_ids array
      if (!apiRequest.item_adjustments.cancel_item_ids) {
        apiRequest.item_adjustments.cancel_item_ids = [];
      }
      
      unreplacedAddons.forEach(addon => {
        apiRequest.item_adjustments.cancel_item_ids.push(addon.uuid);
        console.log(`[DEBUG] Added add-on ${addon.uuid} (${addon.product_code || addon.code}) to cancel_item_ids`);
      });
    }
  } else {
    console.log('[DEBUG] No additional products to process')
  }
}

// Helper function to find all add-ons in the original booking
const findOriginalAddons = (bookingData: any): any[] => {
  const addons: any[] = [];
  
  console.log('[DEBUG] Starting findOriginalAddons function');
  console.log('[DEBUG] Booking data structure keys:', Object.keys(bookingData));
  
  // Check if we have the expected booking data structure
  const hasOutboundSegments = Array.isArray(bookingData.outbound_booking_tariff_segments);
  const hasInboundSegments = Array.isArray(bookingData.inbound_booking_tariff_segments);
  const hasSegments = Array.isArray(bookingData.segments);
  
  console.log('[DEBUG] Booking data structure check:', {
    hasOutboundSegments,
    hasInboundSegments,
    hasSegments,
    outboundSegmentsCount: hasOutboundSegments ? bookingData.outbound_booking_tariff_segments.length : 0,
    inboundSegmentsCount: hasInboundSegments ? bookingData.inbound_booking_tariff_segments.length : 0,
    segmentsCount: hasSegments ? bookingData.segments.length : 0
  });
  
  // FIXED: Use the correct data structure paths
  // First check outbound_booking_tariff_segments
  if (hasOutboundSegments) {
    console.log('[DEBUG] Processing outbound_booking_tariff_segments for add-ons');
    bookingData.outbound_booking_tariff_segments.forEach((segment: any, index: number) => {
      console.log(`[DEBUG] Processing outbound segment ${index}:`, segment.id || 'unknown');
      
      // Check for additional products
      if (Array.isArray(segment.additional_products)) {
        console.log(`[DEBUG] Found ${segment.additional_products.length} additional products in outbound segment ${index}`);
        
        segment.additional_products.forEach((product: any, productIndex: number) => {
          console.log(`[DEBUG] Examining outbound additional product ${productIndex}:`, {
            uuid: product?.uuid,
            code: product?.code || product?.product_code,
            cancelled: product?.cancelled,
            status: product?.status
          });
          
          // Only include products that aren't already cancelled
          const isCancelled = product?.cancelled === true || product?.status === 'cancelled';
          if (product && product.uuid && !isCancelled) {
            addons.push(product);
            console.log(`[DEBUG] Added outbound add-on to collection: ${product.uuid} (${product.product_code || product.code})`);
          } else if (isCancelled) {
            console.log(`[DEBUG] Skipping already cancelled outbound add-on: ${product?.uuid}`);
          }
        });
      } else {
        console.log(`[DEBUG] No additional_products array in outbound segment ${index}`);
      }
      
      // Also check items array for add-ons (some seat products might be here)
      if (Array.isArray(segment.items)) {
        console.log(`[DEBUG] Found ${segment.items.length} items in outbound segment ${index}`);
        
        segment.items.forEach((item: any, itemIndex: number) => {
          // Identify add-ons: not a main ticket product and not cancelled
          const isMainProduct = item?.product_type === 'ST' || 
                               item?.type === 'ST' || 
                               ['STANDARD', 'FLEX', 'MINIPRIS'].includes(item?.product_code || '');
          
          const isCancelled = item?.cancelled === true || item?.status === 'cancelled';
          
          console.log(`[DEBUG] Examining outbound item ${itemIndex}:`, {
            uuid: item?.uuid,
            code: item?.code || item?.product_code,
            isMainProduct,
            isCancelled
          });
          
          if (item && item.uuid && !isMainProduct && !isCancelled) {
            addons.push(item);
            console.log(`[DEBUG] Added outbound item add-on to collection: ${item.uuid} (${item.product_code || item.code})`);
          }
        });
      } else {
        console.log(`[DEBUG] No items array in outbound segment ${index}`);
      }
    });
  }
  
  // Then check inbound_booking_tariff_segments
  if (hasInboundSegments) {
    console.log('[DEBUG] Processing inbound_booking_tariff_segments for add-ons');
    bookingData.inbound_booking_tariff_segments.forEach((segment: any, index: number) => {
      console.log(`[DEBUG] Processing inbound segment ${index}:`, segment.id || 'unknown');
      
      // Check for additional products
      if (Array.isArray(segment.additional_products)) {
        console.log(`[DEBUG] Found ${segment.additional_products.length} additional products in inbound segment ${index}`);
        
        segment.additional_products.forEach((product: any, productIndex: number) => {
          console.log(`[DEBUG] Examining inbound additional product ${productIndex}:`, {
            uuid: product?.uuid,
            code: product?.code || product?.product_code,
            cancelled: product?.cancelled,
            status: product?.status
          });
          
          // Only include products that aren't already cancelled
          const isCancelled = product?.cancelled === true || product?.status === 'cancelled';
          if (product && product.uuid && !isCancelled) {
            addons.push(product);
            console.log(`[DEBUG] Added inbound add-on to collection: ${product.uuid} (${product.product_code || product.code})`);
          } else if (isCancelled) {
            console.log(`[DEBUG] Skipping already cancelled inbound add-on: ${product?.uuid}`);
          }
        });
      } else {
        console.log(`[DEBUG] No additional_products array in inbound segment ${index}`);
      }
      
      // Also check items array for add-ons (some seat products might be here)
      if (Array.isArray(segment.items)) {
        console.log(`[DEBUG] Found ${segment.items.length} items in inbound segment ${index}`);
        
        segment.items.forEach((item: any, itemIndex: number) => {
          // Identify add-ons: not a main ticket product and not cancelled
          const isMainProduct = item?.product_type === 'ST' || 
                               item?.type === 'ST' || 
                               ['STANDARD', 'FLEX', 'MINIPRIS'].includes(item?.product_code || '');
          
          const isCancelled = item?.cancelled === true || item?.status === 'cancelled';
          
          console.log(`[DEBUG] Examining inbound item ${itemIndex}:`, {
            uuid: item?.uuid,
            code: item?.code || item?.product_code,
            isMainProduct,
            isCancelled
          });
          
          if (item && item.uuid && !isMainProduct && !isCancelled) {
            addons.push(item);
            console.log(`[DEBUG] Added inbound item add-on to collection: ${item.uuid} (${item.product_code || item.code})`);
          }
        });
      } else {
        console.log(`[DEBUG] No items array in inbound segment ${index}`);
      }
    });
  }
  
  // For backward compatibility, also check the old structure if present
  if (hasSegments) {
    console.log('[DEBUG] Processing legacy segments structure for add-ons');
    bookingData.segments.forEach((segment: any, index: number) => {
      console.log(`[DEBUG] Processing legacy segment ${index}`);
      
      // Check for additional products
      if (Array.isArray(segment.additional_products)) {
        segment.additional_products.forEach((product: any) => {
          // Only include products that aren't already cancelled
          if (product && product.uuid && product.status !== 'cancelled') {
            addons.push(product);
            console.log(`[DEBUG] Found legacy add-on: ${product.uuid} (${product.product_code || product.code})`);
          }
        });
      }
      
      // Also check items array for add-ons
      if (Array.isArray(segment.items)) {
        segment.items.forEach((item: any) => {
          // Identify add-ons: not a main ticket product and not cancelled
          const isMainProduct = item?.product_type === 'ST' || 
                               item?.type === 'ST' || 
                               ['STANDARD', 'FLEX', 'MINIPRIS'].includes(item?.product_code || '');
          
          if (item && item.uuid && !isMainProduct && item.status !== 'cancelled') {
            addons.push(item);
            console.log(`[DEBUG] Found legacy add-on item: ${item.uuid} (${item.product_code || item.code})`);
          }
        });
      }
    });
  }
  
  // Deduplicate add-ons by UUID
  const uniqueAddons = Array.from(new Map(addons.map(addon => [addon.uuid, addon])).values());
  
  console.log(`[DEBUG] Total add-ons found: ${uniqueAddons.length} (after deduplication from ${addons.length})`);
  if (uniqueAddons.length > 0) {
    console.log('[DEBUG] Add-on UUIDs:', uniqueAddons.map(addon => addon.uuid));
  }
  
  return uniqueAddons;
}
